import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { FaCheck, FaEdit, FaEye, FaFileExcel, FaFilePdf, FaFileUpload, FaPlus, FaRobot, FaTrashAlt } from 'react-icons/fa';
import { acceptanceCriteriaAPI, aiAPI, apiMasterAPI, cleaningConfigAPI, equipmentAPI, macoAPI, productsAPI, resultsAPI, samplingPlansAPI, settingsAPI, commercialConfigAPI } from '../services/api';
import ChangeReferenceModal from '../components/ChangeReferenceModal';
import { STANDARD_MATERIALS_OF_CONSTRUCTION } from '../utils/materialOptions';
import { stampPdfFooter } from '../utils/reportProfiles';
import useApprovalRights from '../hooks/useApprovalRights';
import StatusBadge from '../components/StatusBadge';
import { ESignModal, Modal, ModalFooter } from '../components/Modals';
import VersionHistoryModal from '../components/VersionHistoryModal';
import { useModuleAccess } from '../utils/moduleAccess';
import { ActionsMenu } from '../components/ActionsMenu';
import FilterStatCards, { approvalStatusGroups, matchesGroup } from '../components/FilterStatCards';
import { FaClipboardList } from 'react-icons/fa';
import AIResultPanel from '../components/AIResultPanel';
import useAIAction from '../hooks/useAIAction';
import TablePagination from '../components/TablePagination';

const TRAIN_AREA_COMPENSATION_SETTING_KEY = 'train_area_compensation_percent';
const SAMPLE_TYPES = ['Swab', 'Rinse', 'Micro', 'Visual'];

const APPROVAL_DECISIONS = [
  { value: 'approve', label: 'Approve' },
  { value: 'return', label: 'Return to Draft' },
  { value: 'reject', label: 'Reject' },
];

const toNum = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getRows = (payload) => {
  if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Same formulas as Acceptance Criteria — kept in sync there so a Sampling Plan's
// auto-fetched limit always matches what was actually approved for that part.
// Swab limit is the SAME for every part (uses the AC record's plan-level MAR/area/diluent);
// rinse limit is per-part (uses that part's own rinse surface area / rinse volume).
const calcSwabApiLimit = (mar, swabArea, swabDiluent) => {
  const m = parseFloat(mar), a = parseFloat(swabArea), v = parseFloat(swabDiluent);
  if (!m || !a || !v) return null;
  return m * a / v;
};
const calcRinseApiLimit = (row, mar) => {
  const m = parseFloat(mar);
  const sa = parseFloat(row?.rinse_sa);
  const v = parseFloat(row?.rinse_vol);
  if (!m || !sa || !v) return null;
  return m * sa / v;
};

const fmt = (value, digits = 4) => toNum(value, 0).toFixed(digits);

const createCriterion = (overrides = {}) => ({
  limit_type: 'Chemical',
  criterion_label: '',
  sample_medium: '',
  limit_value: '',
  limit_unit: '',
  acceptance_criteria_ref: '',
  recovery_percent: '',
  include_recovery_in_report: false,
  include_in_report: true,
  notes: '',
  // Traceability back to the ac_equipment_criteria master row this value was copied from —
  // a snapshot, not a live link, so approved plans stay frozen (see backend/routes/samplingPlans.js).
  source_criteria_id: null,
  source_master_updated_at: null,
  ...overrides
});

const createLocation = (overrides = {}) => ({
  equipment_part_id: null,
  train_member_id: null,
  equipment_id: null,
  location_code: '',
  location_name: '',
  location_type: 'Equipment',
  surface_type: '',
  sample_type: 'Swab',
  sample_category: '',
  no_of_locations: 1,
  rationale: '',
  acceptance_criteria_ref: '',
  residue_limit: '',
  residue_limit_unit: 'mcg/cm2',
  recovery_percent: '',
  include_recovery_in_report: false,
  include_in_report: true,
  is_manual: false,
  notes: '',
  metadata: {},
  merged_part_ids: [],
  requires_visual: true,
  include_micro_rinse: false,
  acceptance_criteria: [
    VISUAL_CRITERION(),
    createCriterion({ limit_type: 'Chemical', criterion_label: 'Residue Limit', sample_medium: 'Swab', limit_unit: 'mcg/cm2' })
  ],
  ...overrides
});

const createEmptyPlan = () => ({
  plan_id: null,
  plan_code: '',
  study_type: 'CV',
  train_id: '',
  equipment_id: '',
  previous_api_id: '',
  next_product_id: '',
  governing_maco: '',
  total_surface_area: '',
  effective_surface_area: '',
  mar_mcg_cm2: '',
  swab_concentration_ppm: '',
  least_rinse_concentration_ppm: '',
  remarks: '',
  no_of_batches: 3,
  hold_time_intervals: ['Day 3', 'Day 6', 'Day 9'],
  predecessor_plan_id: '',
  locations: []
});

// DEHT: which study type must be completed before this one starts, per the
// DEHT (dirty hold time) -> Cleaning Validation (CV) -> CEHT (clean hold time) sequence.
const PREDECESSOR_STUDY_TYPE = { CV: 'DEHT', CEHT: 'CV' };

const VISUAL_CRITERION = () => createCriterion({
  limit_type: 'Visual', criterion_label: 'Visual Inspection', sample_medium: 'Visual', limit_unit: 'Pass/Fail'
});

// Visual inspection is the first-pass criterion for every location (equipment part or
// non-contact surface) — it is prepended ahead of any chemical/microbial criteria unless
// explicitly opted out (e.g. a merged rinse point whose sub-parts were already inspected
// individually).
const DEGRADANT_CRITERION = () => createCriterion({
  limit_type: 'Chemical', criterion_label: 'Degradant', sample_medium: 'Swab', limit_unit: 'ppm',
  notes: 'Required — API/product residue may remain at this location (DEHT)'
});

const DETERGENT_CRITERION = (sampleMedium) => createCriterion({
  limit_type: 'Detergent', criterion_label: 'Detergent Residue', sample_medium: sampleMedium, limit_unit: sampleMedium === 'Rinse' ? 'ppm' : 'mcg/cm2',
  notes: 'Cleaning agent carryover — separate limit from product/API residue'
});

const defaultCriteriaBySampleType = (sampleType, options = {}) => {
  const { requiresVisual = true, includeMicroRinse = false, includeDegradant = false, includeDetergent = false } = options;

  if (sampleType === 'Visual') return [VISUAL_CRITERION()];

  const base = (() => {
    switch (sampleType) {
      case 'Rinse': {
        const rinseCriteria = [
          createCriterion({ limit_type: 'Chemical', criterion_label: 'Residue Limit', sample_medium: 'Rinse', limit_unit: 'ppm' }),
          createCriterion({ limit_type: 'TOC', criterion_label: 'TOC', sample_medium: 'Rinse', limit_unit: 'ppm' }),
          createCriterion({ limit_type: 'Chemical', criterion_label: 'Conductivity', sample_medium: 'Rinse', limit_unit: 'uS/cm' }),
          createCriterion({ limit_type: 'Chemical', criterion_label: 'pH', sample_medium: 'Rinse', limit_unit: 'pH' })
        ];
        if (includeMicroRinse) {
          rinseCriteria.push(createCriterion({ limit_type: 'Microbiological', criterion_label: 'Bioburden (Rinse)', sample_medium: 'Rinse', limit_unit: 'cfu/mL' }));
        }
        if (includeDetergent) rinseCriteria.push(DETERGENT_CRITERION('Rinse'));
        return rinseCriteria;
      }
      // DEHT (dirty equipment hold time) locations sample here: bioburden by BOTH methods
      // (swab and rinse are independent collection techniques, each needs its own
      // acceptance criterion), plus a Degradant swab wherever the surface had direct
      // API/product contact — see PREDECESSOR_STUDY_TYPE.
      case 'Micro': {
        const microCriteria = [
          createCriterion({ limit_type: 'Microbiological', criterion_label: 'Bioburden (Swab Method)', sample_medium: 'Swab', limit_unit: 'cfu/swab' }),
          createCriterion({ limit_type: 'Microbiological', criterion_label: 'Bioburden (Rinse Method)', sample_medium: 'Rinse', limit_unit: 'cfu/mL' })
        ];
        if (includeDegradant) microCriteria.push(DEGRADANT_CRITERION());
        return microCriteria;
      }
      default: {
        const swabCriteria = [
          createCriterion({ limit_type: 'Chemical', criterion_label: 'Residue Limit', sample_medium: 'Swab', limit_unit: 'mcg/cm2' }),
          createCriterion({ limit_type: 'TOC', criterion_label: 'TOC', sample_medium: 'Swab', limit_unit: 'ppm' }),
          createCriterion({ limit_type: 'Chemical', criterion_label: 'Conductivity', sample_medium: 'Swab', limit_unit: 'uS/cm' }),
          createCriterion({ limit_type: 'Chemical', criterion_label: 'pH', sample_medium: 'Swab', limit_unit: 'pH' }),
          createCriterion({ limit_type: 'Chemical', criterion_label: 'Toxicity', sample_medium: 'Swab', limit_unit: 'ppm' })
        ];
        if (includeDetergent) swabCriteria.push(DETERGENT_CRITERION('Swab'));
        return swabCriteria;
      }
    }
  })();
  return requiresVisual ? [VISUAL_CRITERION(), ...base] : base;
};

const SamplingPlans = () => {
  const moduleOn = useModuleAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planStatusCard, setPlanStatusCard] = useState('All');
  const [planTypeFilter, setPlanTypeFilter] = useState('');
  const [planListPage, setPlanListPage] = useState(1);
  const [planListPageSize, setPlanListPageSize] = useState(25);
  const [results, setResults] = useState([]);
  const [products, setProducts] = useState([]);
  const [apis, setApis] = useState([]);
  const [trains, setTrains] = useState([]);
  const [trainMembers, setTrainMembers] = useState([]);
  const [form, setForm] = useState(createEmptyPlan());
  const initialPlanRef = useRef('');

  const [trainAreaCompPercent, setTrainAreaCompPercent] = useState(0);
  const [numberingConfig, setNumberingConfig] = useState({ locationPrefix: 'LOC', locationPadding: 3, samplePrefix: 'SMP', samplePadding: 4, planPrefix: 'SP', planPadding: 4 });
  const [generatingLocationCode, setGeneratingLocationCode] = useState(null);
  const [visualLabels, setVisualLabels] = useState({ pass: 'Pass', fail: 'Fail' });
  const [acRecord, setAcRecord] = useState(null);
  // equipment_id -> true for equipment actually cleaned with a detergent per Cleaning
  // Config — the Detergent Residue criterion is only added to a location whose OWN
  // equipment is in this map, not line-wide just because some other equipment uses one.
  const [equipmentDetergentMap, setEquipmentDetergentMap] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [activeLocationGroup, setActiveLocationGroup] = useState('');
  const [expandedEquipment, setExpandedEquipment] = useState({});
  const [uploadingPhotoKey, setUploadingPhotoKey] = useState(null);
  const [newIntervalLabel, setNewIntervalLabel] = useState('');

  // List / builder page mode — see the "list type + modal" layout in the header form.
  const [viewMode, setViewMode] = useState('list');
  const [showNewPlanModal, setShowNewPlanModal] = useState(false);
  const [newPlanDraft, setNewPlanDraft] = useState({ study_type: 'CV', train_id: '', source_plan_id: '' });
  const [creatingPlan, setCreatingPlan] = useState(false);
  const autoPopulateNewPlanRef = useRef(false);

  // Acceptance-criterion entry modal — { mode:'add'|'edit', groupEntries, locationIndex, criterionIndex, draft }
  const [criterionModal, setCriterionModal] = useState(null);

  // Configurable approval workflow (Super Admin setting) — see loadData below.
  const [approvalRequired, setApprovalRequired] = useState(true);
  const ai = useAIAction();

  // E-sign
  const [showESign, setShowESign] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [qmsLinked, setQmsLinked] = useState(false);
  const [changeRefGate, setChangeRefGate] = useState(false);
  const [changeRefBusy, setChangeRefBusy] = useState(false);
  const [pendingChangeRef, setPendingChangeRef] = useState(null);

  useEffect(() => {
    commercialConfigAPI.getRuntime().then((res) => {
      const integrations = res?.data?.data?.integrations || {};
      setQmsLinked(!!integrations.aiventra_qms?.features?.change_control_link);
    }).catch(() => {});
  }, []);
  const [eSignMode, setESignMode] = useState('submit');
  const [eSignData, setESignData] = useState({ password: '', comment: '', decision: 'approve' });
  const [eSigning, setESigning] = useState(false);
  const passwordRef = useRef(null);

  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const role = String(user.role || '').trim().toLowerCase();
  const isSuperAdmin = role === 'super admin';
  const isAdmin = role === 'admin' || isSuperAdmin;
  const { canEarlier: canProductApprove, canFinal: canQaApprove, earlierLabel, finalLabel } = useApprovalRights('sampling_plan.approval');
  const status = form.status || '';
  const canEdit = !approvalRequired || !status || status === 'Draft';

  useEffect(() => { if (showESign) setTimeout(() => passwordRef.current?.focus(), 80); }, [showESign]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const activeLine = localStorage.getItem('activeLine') || '';
        const activePlant = localStorage.getItem('activePlant') || '';
        const [plansRes, trainsRes, apisRes, productsRes, settingsRes, acRes, resultsRes, ccRes] = await Promise.all([
          samplingPlansAPI.getAll(),
          equipmentAPI.getTrains(),
          apiMasterAPI.getAll({ limit: 5000 }),
          productsAPI.getAll({ limit: 5000 }),
          settingsAPI.getAll(),
          (activeLine && activeLine !== 'All')
            ? acceptanceCriteriaAPI.getCriteria({ plant_code: activePlant, production_line: activeLine, approved_only: true })
            : Promise.resolve(null),
          resultsAPI.getAll(),
          cleaningConfigAPI.getAll((activeLine && activeLine !== 'All') ? { line_id: activeLine } : {}).catch(() => ({ data: { data: [] } }))
        ]);
        setAcRecord(acRes?.data?.data || null);
        const detergentMap = {};
        getRows(ccRes).forEach((row) => {
          if (row.equipment_id && row.detergent_used && row.detergent_name) detergentMap[row.equipment_id] = true;
        });
        setEquipmentDetergentMap(detergentMap);

        const settings = settingsRes?.data?.data || {};
        const loadedPlans = getRows(plansRes);
        const loadedTrains = getRows(trainsRes);
        const loadedApis = getRows(apisRes);
        const loadedProducts = getRows(productsRes);

        setPlans(loadedPlans);
        setResults(getRows(resultsRes));
        setTrains(loadedTrains);
        setApis(loadedApis);
        setProducts(loadedProducts);
        setTrainAreaCompPercent(Math.max(0, toNum(settings[TRAIN_AREA_COMPENSATION_SETTING_KEY], 0)));

        setNumberingConfig({
          locationPrefix: settings['numbering_location_prefix'] || 'LOC',
          locationPadding: Math.max(1, toNum(settings['numbering_location_padding'], 3)),
          samplePrefix: settings['numbering_sample_prefix'] || 'SMP',
          samplePadding: Math.max(1, toNum(settings['numbering_sample_padding'], 4)),
          planPrefix: settings['numbering_plan_prefix'] || 'SP',
          planPadding: Math.max(1, toNum(settings['numbering_plan_padding'], 4))
        });

        setVisualLabels({
          pass: settings['visual_limit_pass_label'] || 'Pass',
          fail: settings['visual_limit_fail_label'] || 'Fail'
        });

        setApprovalRequired(settings['workflow_approval_required_sampling_plan'] !== 'false');
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load sampling plan data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    const loadMembers = async () => {
      if (!form.train_id) {
        setTrainMembers([]);
        return;
      }

      setTrainMembers([]);
      try {
        const res = await equipmentAPI.getTrainMembers(form.train_id);
        const members = getRows(res);
        setTrainMembers(members);
        setExpandedEquipment(Object.fromEntries(members.map((member) => [`equip-${member.equipment_id}`, true])));
      } catch (error) {
        setTrainMembers([]);
        toast.error(error?.response?.data?.message || 'Failed to load train members');
      }
    };

    loadMembers();
  }, [form.train_id]);

  const selectedTrain = useMemo(
    () => trains.find((train) => String(train.train_id) === String(form.train_id)) || null,
    [trains, form.train_id]
  );

  const productTrainIds = (product) => [...new Set([
    ...(product?.linked_trains || []).map((link) => String(link.train_id)).filter(Boolean),
    ...(product?.equipment_train_id ? [String(product.equipment_train_id)] : [])
  ])];
  const productsOnSelectedTrain = useMemo(
    () => products.filter((product) => productTrainIds(product).includes(String(form.train_id))),
    [products, form.train_id]
  );
  const linkedTrainCoverage = useMemo(() => {
    if (!form.train_id || !productsOnSelectedTrain.length) return [];
    const relatedTrainIds = new Set([String(form.train_id)]);
    productsOnSelectedTrain.forEach((product) => productTrainIds(product).forEach((id) => relatedTrainIds.add(id)));
    return trains.filter((train) => relatedTrainIds.has(String(train.train_id))).map((train) => ({
      train,
      plan: plans.find((plan) => String(plan.train_id) === String(train.train_id)
        && String(plan.study_type) === String(form.study_type)) || null
    }));
  }, [form.train_id, productsOnSelectedTrain, trains, plans, form.study_type]);

  // Auto-worst-case pair for the selected train, from MACO's own matrix scan
  // (the real lowest-MAR pair) rather than the weighted-score ranking the
  // WorstCase module uses — that ranking is a proxy score and can disagree
  // with the actual governing MACO number. Only fetched for a brand-new plan.
  const [liveWorstCase, setLiveWorstCase] = useState(null);
  useEffect(() => {
    if (form.plan_id || !form.train_id) { setLiveWorstCase(null); return; }
    let cancelled = false;
    macoAPI.getAutoWorstCase(form.train_id)
      .then((res) => { if (!cancelled) setLiveWorstCase(res?.data?.data || null); })
      .catch(() => { if (!cancelled) setLiveWorstCase(null); });
    return () => { cancelled = true; };
  }, [form.train_id, form.plan_id]);

  // Default Previous API + Next Product to the train's worst-case (lowest MAR)
  // pair on a brand-new plan — the Approved pair if one exists, else the
  // best draft pick flagged provisional. The user can still override either
  // field; never touches an already-loaded plan or a value already picked.
  useEffect(() => {
    if (form.plan_id || !liveWorstCase) return;
    const pick = liveWorstCase.approved_worst_case || liveWorstCase.worst_case;
    if (!pick) return;
    setForm((prev) => {
      if (prev.previous_api_id && prev.next_product_id) return prev;
      const next = { ...prev };
      if (!next.previous_api_id && apis.some((api) => String(api.api_id) === String(pick.api_id))) next.previous_api_id = String(pick.api_id);
      if (!next.next_product_id && products.some((p) => String(p.product_id) === String(pick.product_id))) next.next_product_id = String(pick.product_id);
      return next;
    });
  }, [liveWorstCase, form.plan_id, apis, products]);

  // Pharma convention: a cleaning validation study needs at least 3 consecutive QA-Approved
  // batches per location before it's considered complete — this is derived purely from the
  // Results module (distinct approved batch_number per location_id), no schema change needed.
  const MIN_BATCHES_REQUIRED = 3;
  const batchProgressByLocation = useMemo(() => {
    if (!form.plan_id) return {};
    const approvedForPlan = results.filter((r) =>
      String(r.plan_id) === String(form.plan_id) && r.status === 'QA Approved' && r.batch_number
    );
    const byLocation = {};
    approvedForPlan.forEach((r) => {
      const key = String(r.location_id);
      if (!byLocation[key]) byLocation[key] = new Set();
      byLocation[key].add(r.batch_number);
    });
    const result = {};
    Object.keys(byLocation).forEach((key) => { result[key] = byLocation[key].size; });
    return result;
  }, [results, form.plan_id]);

  // DEHT -> Cleaning Validation (CV) -> CEHT sequence: which study type (if any) must be
  // completed on this train before this plan's study type can start.
  const predecessorStudyType = PREDECESSOR_STUDY_TYPE[form.study_type] || null;
  const predecessorCandidates = useMemo(() => {
    if (!predecessorStudyType || !form.train_id) return [];
    return plans.filter((plan) =>
      plan.study_type === predecessorStudyType &&
      String(plan.train_id) === String(form.train_id) &&
      String(plan.plan_id) !== String(form.plan_id || '')
    );
  }, [plans, predecessorStudyType, form.train_id, form.plan_id]);
  const predecessorPlan = useMemo(
    () => predecessorCandidates.find((plan) => String(plan.plan_id) === String(form.predecessor_plan_id)) || null,
    [predecessorCandidates, form.predecessor_plan_id]
  );

  // DEHT and CEHT are both hold-time studies: same location re-sampled at configured
  // intervals, Micro-only by default. Only DEHT additionally requires the Degradant test
  // (API/product residue is a dirty-equipment concern — CEHT samples already-cleaned equipment).
  const isHoldTimeStudy = form.study_type === 'DEHT' || form.study_type === 'CEHT';

  // Left-panel tree: equipment -> sub-parts, expandable. Each node's `key` is how
  // locations are grouped/matched back to it (see nodeKeyForLocation below).
  const equipmentTree = useMemo(() => trainMembers.map((member) => {
    const memberId = member.train_member_id || member.member_id || null;
    const parts = parseJsonArray(member.equipment_parts).filter((part) => part.part_id);
    return {
      key: `equip-${member.equipment_id}`,
      equipment_id: member.equipment_id || null,
      member_id: memberId,
      label: `${member.equipment_code || ''} ${member.equipment_name || ''}`.trim() || 'Equipment',
      equipment_code: member.equipment_code || '',
      material: member.equipment_type || '',
      member,
      parts: parts.map((part) => ({
        key: `part-${part.part_id}`,
        part_id: part.part_id,
        equipment_id: member.equipment_id || null,
        member_id: memberId,
        label: part.part_name || `Part ${part.part_id}`,
        material: part.material_of_construction || '',
        equipment_code: member.equipment_code || '',
        part_code: part.part_code || '',
        member,
        part
      }))
    };
  }), [trainMembers]);
  const trainLocationCount = equipmentTree.reduce((count, equipment) => count + Math.max(equipment.parts.length, 1), 0);

  // Additional (non-equipment-train) locations already added to the plan — e.g. Floor,
  // Wall — grouped by their stable manual_key so each gets its own tree entry too.
  const manualNodes = useMemo(() => {
    const seen = new Map();
    form.locations.forEach((location) => {
      if (location.equipment_part_id || location.equipment_id) return;
      const manualKey = location?.metadata?.manual_key;
      if (!manualKey || seen.has(manualKey)) return;
      seen.set(manualKey, {
        key: `manual-${manualKey}`,
        manual_key: manualKey,
        label: location.location_name || 'Additional Location',
        location_type: location.location_type || 'Area'
      });
    });
    return Array.from(seen.values());
  }, [form.locations]);

  // ---- Acceptance Criteria auto-fetch: match this train's parts back to the saved
  // AC record for the active production line, and compute the same limit formulas
  // AcceptanceCriteria.js uses, so Sampling Plans never re-derives numbers by hand.
  const findAcSwabRow = (partId) => parseJsonArray(acRecord?.swab_criteria).find((r) => String(r.part_id) === String(partId)) || null;
  const findAcRinseRow = (partId) => parseJsonArray(acRecord?.rinse_criteria).find((r) => String(r.part_id) === String(partId)) || null;

  // The live MACO MAR for the currently-selected (Previous API, Next Product) pair,
  // valid only when that pair is exactly the train's computed worst case (so the
  // number is guaranteed fresh) — if the user overrides to a different pair, this
  // is left null and the Acceptance Criteria master snapshot is used instead,
  // rather than showing a stale MAR from the original worst-case pair.
  const liveWorstCasePickForSelectedPair = useMemo(() => {
    const pick = liveWorstCase?.approved_worst_case || liveWorstCase?.worst_case;
    if (!pick || !form.previous_api_id || !form.next_product_id) return null;
    if (String(pick.api_id) !== String(form.previous_api_id) || String(pick.product_id) !== String(form.next_product_id)) return null;
    return pick;
  }, [liveWorstCase, form.previous_api_id, form.next_product_id]);
  const liveMarForSelectedPair = liveWorstCasePickForSelectedPair?.mar > 0 ? liveWorstCasePickForSelectedPair.mar : null;
  const liveDetergentMarForSelectedPair = liveWorstCasePickForSelectedPair?.detergentMar > 0 ? liveWorstCasePickForSelectedPair.detergentMar : null;
  const effectiveMarPerCm2 = liveMarForSelectedPair ?? acRecord?.mar_per_cm2;
  const effectiveDetergentMarPerCm2 = liveDetergentMarForSelectedPair ?? acRecord?.detergent_mar_per_cm2;
  const acSwabLimit = useMemo(
    () => (effectiveMarPerCm2 != null && acRecord ? calcSwabApiLimit(effectiveMarPerCm2, acRecord.swab_area_cm2, acRecord.swab_diluent_ml) : null),
    [effectiveMarPerCm2, acRecord]
  );
  // Detergent carryover (from the equipment's cleaning agent, not the product API) gets its own
  // MAR at the Acceptance Criteria level (detergent_mar_per_cm2) — same swab/rinse formulas, a
  // separate number. Only sampled for when that limit is actually set up for this line.
  const detergentActive = toNum(effectiveDetergentMarPerCm2, 0) > 0;
  const acDetergentSwabLimit = useMemo(
    () => (effectiveDetergentMarPerCm2 != null && acRecord ? calcSwabApiLimit(effectiveDetergentMarPerCm2, acRecord.swab_area_cm2, acRecord.swab_diluent_ml) : null),
    [effectiveDetergentMarPerCm2, acRecord]
  );

  const previousApi = useMemo(
    () => apis.find((api) => String(api.api_id) === String(form.previous_api_id)) || null,
    [apis, form.previous_api_id]
  );

  // Governing MACO/MAR is no longer recomputed here from Previous API + Next Product — it's
  // pulled straight from the Acceptance Criteria master (acRecord.mar_per_cm2), which is
  // itself sourced from the MACO Calculator. Governing MACO (mg) is back-derived from that
  // MAR for display only (mar = maco*1000/effectiveArea).
  const totalArea = useMemo(
    () => trainMembers.reduce((sum, member) => sum + toNum(member.total_area, 0), 0),
    [trainMembers]
  );
  const effectiveArea = totalArea * (1 + (trainAreaCompPercent / 100));
  const marMcgCm2 = toNum(acRecord?.mar_per_cm2, 0);
  const governingMaco = effectiveArea > 0 ? (marMcgCm2 * effectiveArea) / 1000 : 0;
  const swabPpm = acSwabLimit || 0;

  const rinseRows = useMemo(
    () => trainMembers.map((member) => {
      const totalMemberArea = toNum(member.total_area, 0);
      const rinseVolume = Math.max(0.000001, toNum(member.rinse_volume_ml, 5000));
      return {
        member_id: member.train_member_id || member.member_id,
        equipment_name: `${member.equipment_code || ''} ${member.equipment_name || ''}`.trim(),
        area: totalMemberArea,
        rinse_volume_ml: rinseVolume,
        ppm: rinseVolume > 0 ? (totalMemberArea * marMcgCm2) / rinseVolume : 0
      };
    }),
    [trainMembers, marMcgCm2]
  );

  const leastRinse = useMemo(() => {
    const valid = rinseRows.map((row) => row.ppm).filter((value) => value > 0);
    return valid.length ? Math.min(...valid) : 0;
  }, [rinseRows]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      governing_maco: governingMaco ? Number(governingMaco.toFixed(6)) : '',
      total_surface_area: totalArea ? Number(totalArea.toFixed(2)) : '',
      effective_surface_area: effectiveArea ? Number(effectiveArea.toFixed(2)) : '',
      mar_mcg_cm2: marMcgCm2 ? Number(marMcgCm2.toFixed(6)) : '',
      swab_concentration_ppm: swabPpm ? Number(swabPpm.toFixed(6)) : '',
      least_rinse_concentration_ppm: leastRinse ? Number(leastRinse.toFixed(6)) : ''
    }));
  }, [effectiveArea, governingMaco, leastRinse, marMcgCm2, swabPpm, totalArea]);

  // DEHT and CEHT both reuse the same equipment/part locations as CV, but sample them per
  // hold-time interval with Micro (DEHT also adds Degradant on direct-contact surfaces)
  // instead of the Swab chemical panel.
  const buildHoldTimeLocationEntries = ({ equipmentPartId, trainMemberId, equipmentId, locationCode, locationName, surfaceType, rationale, apiContact, metadataExtra }) => {
    const intervals = form.hold_time_intervals?.length ? form.hold_time_intervals : [''];
    const includeDegradant = form.study_type === 'DEHT' && apiContact;
    return intervals.map((intervalLabel) => createLocation({
      equipment_part_id: equipmentPartId,
      train_member_id: trainMemberId,
      equipment_id: equipmentId,
      location_code: locationCode,
      location_name: locationName,
      location_type: 'Equipment',
      surface_type: surfaceType,
      sample_type: 'Micro',
      sample_category: 'Microbiological',
      rationale,
      acceptance_criteria: buildAcAwareCriteria('Micro', equipmentPartId, { requiresVisual: false, includeDegradant }, equipmentId),
      metadata: { ...metadataExtra, api_contact_surface: apiContact, interval_label: intervalLabel }
    }));
  };

  const buildLocationsFromTrain = () => {
    const generated = [];
    trainMembers.forEach((member) => {
      const memberId = member.train_member_id || member.member_id || null;
      const equipmentId = member.equipment_id || null;
      const parts = parseJsonArray(member.equipment_parts);
      if (parts.length) {
        parts.forEach((part, index) => {
          const locationCode = `${member.equipment_code || 'EQ'}-${part.part_code || index + 1}`;
          const locationName = `${member.equipment_name || 'Equipment'} - ${part.part_name || `Part ${index + 1}`}`;
          const surfaceType = part.material_of_construction || member.equipment_type || '';
          const rationale = 'Direct product contact surface from train mapping';
          const metadataExtra = { equipment_code: member.equipment_code || '', part_name: part.part_name || '', contact_type: part.contact_type || '', surface_area: part.surface_area || '' };
          if (isHoldTimeStudy) {
            generated.push(...buildHoldTimeLocationEntries({ equipmentPartId: part.part_id || null, trainMemberId: memberId, equipmentId, locationCode, locationName, surfaceType, rationale, apiContact: true, metadataExtra }));
          } else {
            generated.push(createLocation({
              equipment_part_id: part.part_id || null,
              train_member_id: memberId,
              equipment_id: equipmentId,
              location_code: locationCode,
              location_name: locationName,
              location_type: 'Equipment',
              surface_type: surfaceType,
              sample_type: 'Swab',
              sample_category: 'Chemical',
              rationale,
              acceptance_criteria: buildAcAwareCriteria('Swab', part.part_id, undefined, equipmentId),
              metadata: metadataExtra
            }));
          }
        });
      } else {
        const locationCode = member.equipment_code || '';
        const locationName = member.equipment_name || 'Equipment';
        const surfaceType = member.equipment_type || '';
        const rationale = 'Equipment-level location because no specific part record was found';
        const metadataExtra = { equipment_code: member.equipment_code || '' };
        if (isHoldTimeStudy) {
          generated.push(...buildHoldTimeLocationEntries({ equipmentPartId: null, trainMemberId: memberId, equipmentId, locationCode, locationName, surfaceType, rationale, apiContact: false, metadataExtra }));
        } else {
          generated.push(createLocation({
            train_member_id: memberId,
            equipment_id: equipmentId,
            location_code: locationCode,
            location_name: locationName,
            location_type: 'Equipment',
            surface_type: surfaceType,
            sample_type: 'Swab',
            sample_category: 'Chemical',
            rationale,
            acceptance_criteria: buildAcAwareCriteria('Swab', null, undefined, equipmentId),
            metadata: metadataExtra
          }));
        }
      }
    });

    return generated;
  };

  useEffect(() => {
    if (!autoPopulateNewPlanRef.current || !form.train_id || !trainMembers.length) return;
    autoPopulateNewPlanRef.current = false;
    setForm((prev) => (prev.plan_id || prev.locations.length ? prev : { ...prev, locations: buildLocationsFromTrain() }));
    const firstNode = equipmentTree[0]?.parts[0] || equipmentTree[0] || null;
    setSelectedNode(firstNode);
    if (firstNode) setActiveLocationGroup(`${firstNode.key}-0`);
  }, [form.train_id, trainMembers, equipmentTree]);

  const handlePlanSelect = (planId) => {
    if (!planId) {
      setForm(createEmptyPlan());
      return;
    }

    const selected = plans.find((plan) => String(plan.plan_id) === String(planId));
    if (!selected) return;

    const loaded = {
      ...createEmptyPlan(),
      ...selected,
      train_id: selected.train_id ? String(selected.train_id) : '',
      equipment_id: selected.equipment_id ? String(selected.equipment_id) : '',
      previous_api_id: selected.previous_api_id ? String(selected.previous_api_id) : '',
      next_product_id: selected.next_product_id ? String(selected.next_product_id) : '',
      locations: (selected.locations || []).map((location) => ({
        ...createLocation(),
        ...location,
        acceptance_criteria: (location.acceptance_criteria || []).map((criterion) => createCriterion(criterion))
      }))
    };
    initialPlanRef.current = JSON.stringify(loaded);
    setForm(loaded);
    setViewMode('builder');
  };

  const handleFormField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // ── Tree-node / tick-box location model ──────────────────────────────────────
  // A node (equipment, sub-part, or manual surface) can have one or more location
  // "groups" (the +Add Another Location affordance) sitting at it, and each group can
  // have any combination of sample types ticked on at once — each tick is one entry
  // in form.locations sharing the same node key + group_seq, matching the same
  // plain location-row model the backend/save logic already expects.
  const nodeKeyForLocation = (location) => {
    if (location.equipment_part_id) return `part-${location.equipment_part_id}`;
    if (location.equipment_id) return `equip-${location.equipment_id}`;
    return `manual-${location?.metadata?.manual_key || ''}`;
  };
  const groupSeqOf = (location) => Number(location?.metadata?.group_seq || 0);

  const groupsForNode = (nodeKey) => {
    const seqSet = new Set();
    form.locations.forEach((location) => { if (nodeKeyForLocation(location) === nodeKey) seqSet.add(groupSeqOf(location)); });
    return Array.from(seqSet).sort((a, b) => a - b);
  };

  const locationsInGroup = (nodeKey, groupSeq) =>
    form.locations
      .map((location, index) => ({ location, index }))
      .filter(({ location }) => nodeKeyForLocation(location) === nodeKey && groupSeqOf(location) === groupSeq);

  // Auto-fill limit/unit/visual result from the saved Acceptance Criteria record for
  // this part, using the exact same formulas AcceptanceCriteria.js computes with —
  // this is the "why re-enter the limit when Acceptance Criteria already has it" fix.
  const buildAcAwareCriteria = (sampleType, partId, options, equipmentId) => {
    // Detergent Residue only applies to a location whose OWN equipment is actually
    // detergent-cleaned (Cleaning Config), not just because the line's Acceptance
    // Criteria has a detergent MAR configured from some other equipment.
    const includeDetergent = detergentActive && !!equipmentDetergentMap[equipmentId] && (sampleType === 'Swab' || sampleType === 'Rinse');
    const base = defaultCriteriaBySampleType(sampleType, { ...options, includeDetergent });
    const syncedAt = new Date().toISOString();
    const acSwabRow = partId ? findAcSwabRow(partId) : null;
    const acRinseRow = partId ? findAcRinseRow(partId) : null;
    const acRinseLimit = acRinseRow ? calcRinseApiLimit(acRinseRow, effectiveMarPerCm2) : null;
    const acDetergentRinseLimit = acRinseRow ? calcRinseApiLimit(acRinseRow, effectiveDetergentMarPerCm2) : null;
    const acVisual = (acSwabRow || acRinseRow)?.visual_limit;

    // source_criteria_id is stamped on anything actually copied from the master record, so
    // the backend can trace it back and the UI can later flag "master changed since this was
    // copied" (see acRecord.updated_at vs. the location's source_synced_at once saved).
    return base.map((criterion) => {
      if (criterion.limit_type === 'Visual') {
        return acVisual ? { ...criterion, limit_value: acVisual, notes: 'Auto-fetched from Acceptance Criteria', source_criteria_id: acRecord?.id || null, source_master_updated_at: acRecord?.updated_at || null, source_synced_at: syncedAt } : criterion;
      }
      if (sampleType === 'Swab' && criterion.criterion_label === 'Residue Limit' && acSwabLimit != null) {
        return { ...criterion, limit_value: Number(acSwabLimit.toFixed(4)), limit_unit: 'mcg/mL', notes: liveMarForSelectedPair != null ? 'Auto-fetched from MACO (live)' : 'Auto-fetched from Acceptance Criteria', source_criteria_id: acRecord?.id || null, source_master_updated_at: acRecord?.updated_at || null, source_synced_at: syncedAt };
      }
      if (sampleType === 'Rinse' && criterion.criterion_label === 'Residue Limit' && acRinseLimit != null) {
        return { ...criterion, limit_value: Number(acRinseLimit.toFixed(4)), limit_unit: 'ppm', notes: liveMarForSelectedPair != null ? 'Auto-fetched from MACO (live)' : 'Auto-fetched from Acceptance Criteria', source_criteria_id: acRecord?.id || null, source_master_updated_at: acRecord?.updated_at || null, source_synced_at: syncedAt };
      }
      if (sampleType === 'Swab' && criterion.criterion_label === 'Detergent Residue' && acDetergentSwabLimit != null) {
        return { ...criterion, limit_value: Number(acDetergentSwabLimit.toFixed(4)), limit_unit: 'mcg/mL', notes: liveDetergentMarForSelectedPair != null ? 'Auto-fetched from MACO (live, detergent MAR)' : 'Auto-fetched from Acceptance Criteria (detergent MAR)', source_criteria_id: acRecord?.id || null, source_master_updated_at: acRecord?.updated_at || null, source_synced_at: syncedAt };
      }
      if (sampleType === 'Rinse' && criterion.criterion_label === 'Detergent Residue' && acDetergentRinseLimit != null) {
        return { ...criterion, limit_value: Number(acDetergentRinseLimit.toFixed(4)), limit_unit: 'ppm', notes: liveDetergentMarForSelectedPair != null ? 'Auto-fetched from MACO (live, detergent MAR)' : 'Auto-fetched from Acceptance Criteria (detergent MAR)', source_criteria_id: acRecord?.id || null, source_master_updated_at: acRecord?.updated_at || null, source_synced_at: syncedAt };
      }
      return criterion;
    });
  };

  const generateSampleId = async (sampleType) => {
    try {
      const res = await settingsAPI.nextSequence('sample');
      const next = res?.data?.data?.next;
      const typeSuffix = { Swab: 'SWB', Rinse: 'RNS', Micro: 'MIC', Visual: 'VIS' }[sampleType] || sampleType.slice(0, 3).toUpperCase();
      return `${numberingConfig.samplePrefix}-${String(next).padStart(numberingConfig.samplePadding, '0')}-${typeSuffix}`;
    } catch {
      return '';
    }
  };

  // Tick ON creates a new location entry (with its own auto-generated Sample ID);
  // tick OFF removes that entry. Nothing else in the group is touched.
  const toggleSampleType = async (nodeInfo, groupSeq, sampleType) => {
    const nodeKey = nodeInfo.key;
    const existing = locationsInGroup(nodeKey, groupSeq).find(({ location }) => location.sample_type === sampleType);

    if (existing) {
      setForm((prev) => ({ ...prev, locations: prev.locations.filter((_, i) => i !== existing.index) }));
      return;
    }

    const sampleId = await generateSampleId(sampleType);
    const siblingCount = groupSeq; // group 0 = base code, group 1+ get -L{n} suffix
    const baseCode = nodeInfo.part_id
      ? `${nodeInfo.equipment_code || 'EQ'}-${nodeInfo.part_code || nodeInfo.part_id}`
      : (nodeInfo.equipment_code || nodeInfo.label || 'LOC');
    const locationCode = siblingCount > 0 ? `${baseCode}-L${siblingCount + 1}` : baseCode;
    const anySiblingInGroup = locationsInGroup(nodeKey, groupSeq)[0]?.location;
    const apiContact = anySiblingInGroup ? isApiContactSurface(anySiblingInGroup) : true;
    const includeDegradant = sampleType === 'Micro' && form.study_type === 'DEHT' && apiContact;

    setForm((prev) => ({
      ...prev,
      locations: [
        ...prev.locations,
        createLocation({
          equipment_part_id: nodeInfo.part_id || null,
          train_member_id: nodeInfo.member_id || null,
          equipment_id: nodeInfo.equipment_id || null,
          location_code: anySiblingInGroup?.location_code || locationCode,
          location_name: anySiblingInGroup?.location_name || (nodeInfo.part_id ? `${nodeInfo.label}` : nodeInfo.label),
          location_type: nodeInfo.part_id || nodeInfo.equipment_id ? 'Equipment' : (nodeInfo.location_type || 'Area'),
          surface_type: anySiblingInGroup?.surface_type || nodeInfo.material || '',
          sample_type: sampleType,
          sample_category: sampleType === 'Micro' ? 'Microbiological' : sampleType === 'Visual' ? 'Visual' : 'Chemical',
          rationale: anySiblingInGroup?.rationale || (nodeInfo.part_id ? 'Direct product contact surface from train mapping' : ''),
          acceptance_criteria: buildAcAwareCriteria(sampleType, nodeInfo.part_id, { requiresVisual: false, includeDegradant }, nodeInfo.equipment_id),
          metadata: {
            group_seq: groupSeq,
            manual_key: nodeInfo.manual_key || null,
            sample_id: sampleId,
            equipment_code: nodeInfo.equipment_code || '',
            part_name: nodeInfo.part_id ? nodeInfo.label : '',
            api_contact_surface: apiContact,
            photo_url: anySiblingInGroup?.metadata?.photo_url || ''
          }
        })
      ]
    }));
    setActiveLocationGroup(`${nodeKey}-${groupSeq}`);
  };

  const addLocationGroup = (nodeInfo) => {
    const nextSeq = (() => {
      const seqs = groupsForNode(nodeInfo.key);
      return seqs.length ? Math.max(...seqs) + 1 : 0;
    })();
    setSelectedNode(nodeInfo);
    setActiveLocationGroup(`${nodeInfo.key}-${nextSeq}`);
    if (isHoldTimeStudy) {
      // DEHT/CEHT groups start from the first configured hold-time interval instead of Visual —
      // see toggleHoldTimeInterval / PREDECESSOR_STUDY_TYPE for the rest of the flow.
      const firstInterval = (form.hold_time_intervals || [])[0];
      if (firstInterval) { toggleHoldTimeInterval(nodeInfo, nextSeq, firstInterval); return; }
    }
    // Ticking Visual by default gives every new group at least one entry to work with.
    toggleSampleType(nodeInfo, nextSeq, 'Visual');
  };

  // ── DEHT / CEHT hold-time studies ────────────────────────────────────────────
  // Same location is re-swabbed at each configured hold-time interval (same location_code,
  // tagged with metadata.interval_label). Only microbial (Bioburden) is required, plus — on
  // DEHT only — a Degradant swab wherever the surface is flagged as having direct API/product
  // contact (CEHT samples equipment that's already been cleaned and validated, so no residue is expected).
  const isApiContactSurface = (location) => location?.metadata?.api_contact_surface !== false;

  const toggleHoldTimeInterval = async (nodeInfo, groupSeq, intervalLabel) => {
    const nodeKey = nodeInfo.key;
    const existing = locationsInGroup(nodeKey, groupSeq)
      .find(({ location }) => location.sample_type === 'Micro' && (location.metadata?.interval_label || '') === intervalLabel);

    if (existing) {
      setForm((prev) => ({ ...prev, locations: prev.locations.filter((_, i) => i !== existing.index) }));
      return;
    }

    const sampleId = await generateSampleId('Micro');
    const siblingCount = groupSeq;
    const baseCode = nodeInfo.part_id
      ? `${nodeInfo.equipment_code || 'EQ'}-${nodeInfo.part_code || nodeInfo.part_id}`
      : (nodeInfo.equipment_code || nodeInfo.label || 'LOC');
    const locationCode = siblingCount > 0 ? `${baseCode}-L${siblingCount + 1}` : baseCode;
    const anySiblingInGroup = locationsInGroup(nodeKey, groupSeq)[0]?.location;
    const apiContact = anySiblingInGroup ? isApiContactSurface(anySiblingInGroup) : true;
    const includeDegradant = form.study_type === 'DEHT' && apiContact;

    setForm((prev) => ({
      ...prev,
      locations: [
        ...prev.locations,
        createLocation({
          equipment_part_id: nodeInfo.part_id || null,
          train_member_id: nodeInfo.member_id || null,
          equipment_id: nodeInfo.equipment_id || null,
          location_code: anySiblingInGroup?.location_code || locationCode,
          location_name: anySiblingInGroup?.location_name || (nodeInfo.part_id ? `${nodeInfo.label}` : nodeInfo.label),
          location_type: nodeInfo.part_id || nodeInfo.equipment_id ? 'Equipment' : (nodeInfo.location_type || 'Area'),
          surface_type: anySiblingInGroup?.surface_type || nodeInfo.material || '',
          sample_type: 'Micro',
          sample_category: 'Microbiological',
          rationale: anySiblingInGroup?.rationale || (nodeInfo.part_id ? 'Direct product contact surface from train mapping' : ''),
          acceptance_criteria: buildAcAwareCriteria('Micro', nodeInfo.part_id, { requiresVisual: false, includeDegradant }, nodeInfo.equipment_id),
          metadata: {
            group_seq: groupSeq,
            manual_key: nodeInfo.manual_key || null,
            sample_id: sampleId,
            equipment_code: nodeInfo.equipment_code || '',
            part_name: nodeInfo.part_id ? nodeInfo.label : '',
            api_contact_surface: apiContact,
            interval_label: intervalLabel,
            photo_url: anySiblingInGroup?.metadata?.photo_url || ''
          }
        })
      ]
    }));
    setActiveLocationGroup(`${nodeKey}-${groupSeq}`);
  };

  // Flips the API-contact flag for every entry in the group and adds/removes the Degradant
  // criterion on each of that group's Micro (interval) entries to match.
  const toggleDehtApiContact = (nodeKey, groupSeq, value) => {
    setForm((prev) => ({
      ...prev,
      locations: prev.locations.map((location) => {
        if (nodeKeyForLocation(location) !== nodeKey || groupSeqOf(location) !== groupSeq) return location;
        const hasDegradant = (location.acceptance_criteria || []).some((c) => c.criterion_label === 'Degradant');
        let acceptance_criteria = location.acceptance_criteria || [];
        if (location.sample_type === 'Micro') {
          if (value && !hasDegradant) acceptance_criteria = [...acceptance_criteria, DEGRADANT_CRITERION()];
          if (!value && hasDegradant) acceptance_criteria = acceptance_criteria.filter((c) => c.criterion_label !== 'Degradant');
        }
        return { ...location, metadata: { ...(location.metadata || {}), api_contact_surface: value }, acceptance_criteria };
      })
    }));
  };

  const addHoldTimeInterval = () => {
    const label = (newIntervalLabel || '').trim();
    if (!label) return;
    if ((form.hold_time_intervals || []).includes(label)) { setNewIntervalLabel(''); return; }
    setForm((prev) => ({ ...prev, hold_time_intervals: [...(prev.hold_time_intervals || []), label] }));
    setNewIntervalLabel('');
  };

  const removeHoldTimeInterval = (label) => {
    setForm((prev) => ({
      ...prev,
      hold_time_intervals: (prev.hold_time_intervals || []).filter((l) => l !== label),
      locations: prev.locations.filter((loc) => !(loc.sample_type === 'Micro' && loc.metadata?.interval_label === label))
    }));
  };

  const removeLocationGroup = (nodeKey, groupSeq) => {
    setForm((prev) => ({
      ...prev,
      locations: prev.locations.filter((location) => !(nodeKeyForLocation(location) === nodeKey && groupSeqOf(location) === groupSeq))
    }));
  };

  const addManualLocationGroup = () => {
    const manualKey = `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const nodeInfo = { key: `manual-${manualKey}`, manual_key: manualKey, label: 'Floor / Wall / External Surface', location_type: 'Area', material: '' };
    setSelectedNode(nodeInfo);
    toggleSampleType(nodeInfo, 0, 'Visual');
  };

  const handleGroupField = (nodeKey, groupSeq, field, value) => {
    setForm((prev) => ({
      ...prev,
      locations: prev.locations.map((location) =>
        (nodeKeyForLocation(location) === nodeKey && groupSeqOf(location) === groupSeq)
          ? field === 'sampling_area_grade'
            ? { ...location, metadata: { ...(location.metadata || {}), sampling_area_grade: value } }
            : { ...location, [field]: value }
          : location
      )
    }));
  };

  const handleGroupPhoto = (nodeKey, groupSeq, file) => {
    if (!file) return Promise.resolve();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        setForm((prev) => ({
          ...prev,
          locations: prev.locations.map((location) =>
            (nodeKeyForLocation(location) === nodeKey && groupSeqOf(location) === groupSeq)
              ? { ...location, metadata: { ...(location.metadata || {}), photo_url: dataUrl } }
              : location
          )
        }));
        resolve();
      };
      reader.onerror = () => resolve();
      reader.readAsDataURL(file);
    });
  };

  // A criterion is stale when the ac_equipment_criteria master row it was copied from has
  // been updated since it was last synced into this plan — a heads-up, not an auto-change
  // (see backend/routes/samplingPlans.js for why this is a snapshot, not a live link).
  const isCriterionStale = (criterion) => {
    if (!criterion?.source_criteria_id || !criterion?.source_master_updated_at || !criterion?.source_synced_at) return false;
    return new Date(criterion.source_master_updated_at).getTime() > new Date(criterion.source_synced_at).getTime();
  };

  const refreshCriterionFromMaster = async (locationIndex, criterionIndex) => {
    try {
      const activeLine = localStorage.getItem('activeLine') || '';
      const activePlant = localStorage.getItem('activePlant') || '';
      const res = await acceptanceCriteriaAPI.getCriteria({ plant_code: activePlant, production_line: activeLine, approved_only: true });
      const freshAc = res?.data?.data || null;
      if (!freshAc) { toast.info('No Acceptance Criteria master record found for this line'); return; }
      setAcRecord(freshAc);

      const location = form.locations[locationIndex];
      const criterion = location?.acceptance_criteria?.[criterionIndex];
      if (!location || !criterion) return;

      const partId = location.equipment_part_id;
      const acSwabRow = partId ? parseJsonArray(freshAc.swab_criteria).find((r) => String(r.part_id) === String(partId)) : null;
      const acRinseRow = partId ? parseJsonArray(freshAc.rinse_criteria).find((r) => String(r.part_id) === String(partId)) : null;
      const freshSwabLimit = calcSwabApiLimit(freshAc.mar_per_cm2, freshAc.swab_area_cm2, freshAc.swab_diluent_ml);
      const freshRinseLimit = acRinseRow ? calcRinseApiLimit(acRinseRow, freshAc.mar_per_cm2) : null;
      const freshDetergentSwabLimit = calcSwabApiLimit(freshAc.detergent_mar_per_cm2, freshAc.swab_area_cm2, freshAc.swab_diluent_ml);
      const freshDetergentRinseLimit = acRinseRow ? calcRinseApiLimit(acRinseRow, freshAc.detergent_mar_per_cm2) : null;
      const acVisual = (acSwabRow || acRinseRow)?.visual_limit;

      let updated = null;
      if (criterion.limit_type === 'Visual' && acVisual) {
        updated = { limit_value: acVisual };
      } else if (location.sample_type === 'Swab' && criterion.criterion_label === 'Residue Limit' && freshSwabLimit != null) {
        updated = { limit_value: Number(freshSwabLimit.toFixed(4)), limit_unit: 'mcg/mL' };
      } else if (location.sample_type === 'Rinse' && criterion.criterion_label === 'Residue Limit' && freshRinseLimit != null) {
        updated = { limit_value: Number(freshRinseLimit.toFixed(4)), limit_unit: 'ppm' };
      } else if (location.sample_type === 'Swab' && criterion.criterion_label === 'Detergent Residue' && freshDetergentSwabLimit != null) {
        updated = { limit_value: Number(freshDetergentSwabLimit.toFixed(4)), limit_unit: 'mcg/mL' };
      } else if (location.sample_type === 'Rinse' && criterion.criterion_label === 'Detergent Residue' && freshDetergentRinseLimit != null) {
        updated = { limit_value: Number(freshDetergentRinseLimit.toFixed(4)), limit_unit: 'ppm' };
      }
      if (!updated) { toast.info('No master value applies to this criterion'); return; }

      setForm((prev) => ({
        ...prev,
        locations: prev.locations.map((loc, idx) => idx !== locationIndex ? loc : ({
          ...loc,
          acceptance_criteria: loc.acceptance_criteria.map((c, cIdx) => cIdx !== criterionIndex ? c : ({
            ...c, ...updated, notes: 'Auto-fetched from Acceptance Criteria', override_justification: '',
            source_criteria_id: freshAc.id, source_master_updated_at: freshAc.updated_at, source_synced_at: new Date().toISOString()
          }))
        }))
      }));
      toast.success('Refreshed from Acceptance Criteria master');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to refresh from master');
    }
  };

  const refreshAllLimitsFromMaster = async () => {
    try {
      const activeLine = localStorage.getItem('activeLine') || '';
      const activePlant = localStorage.getItem('activePlant') || '';
      const res = await acceptanceCriteriaAPI.getCriteria({ plant_code: activePlant, production_line: activeLine, approved_only: true });
      const freshAc = res?.data?.data || null;
      if (!freshAc) { toast.info('No Acceptance Criteria master record found for this line'); return; }
      const swabLimit = calcSwabApiLimit(freshAc.mar_per_cm2, freshAc.swab_area_cm2, freshAc.swab_diluent_ml);
      const detergentSwabLimit = calcSwabApiLimit(freshAc.detergent_mar_per_cm2, freshAc.swab_area_cm2, freshAc.swab_diluent_ml);
      const swabRows = parseJsonArray(freshAc.swab_criteria);
      const rinseRows = parseJsonArray(freshAc.rinse_criteria);
      let updatedCount = 0;
      const syncedAt = new Date().toISOString();
      const refreshedLocations = form.locations.map((location) => {
          const partId = location.equipment_part_id;
          const swabRow = partId ? swabRows.find((row) => String(row.part_id) === String(partId)) : null;
          const rinseRow = partId ? rinseRows.find((row) => String(row.part_id) === String(partId)) : null;
          const visual = (swabRow || rinseRow)?.visual_limit;
          const rinseLimit = rinseRow ? calcRinseApiLimit(rinseRow, freshAc.mar_per_cm2) : null;
          const detergentRinseLimit = rinseRow ? calcRinseApiLimit(rinseRow, freshAc.detergent_mar_per_cm2) : null;
          return {
            ...location,
            acceptance_criteria: (location.acceptance_criteria || []).map((criterion) => {
              let limit = null;
              let unit = criterion.limit_unit;
              if (criterion.limit_type === 'Visual' && visual) limit = visual;
              else if (location.sample_type === 'Swab' && criterion.criterion_label === 'Residue Limit' && swabLimit != null) {
                limit = Number(swabLimit.toFixed(4)); unit = 'mcg/mL';
              } else if (location.sample_type === 'Rinse' && criterion.criterion_label === 'Residue Limit' && rinseLimit != null) {
                limit = Number(rinseLimit.toFixed(4)); unit = 'ppm';
              } else if (location.sample_type === 'Swab' && criterion.criterion_label === 'Detergent Residue' && detergentSwabLimit != null) {
                limit = Number(detergentSwabLimit.toFixed(4)); unit = 'mcg/mL';
              } else if (location.sample_type === 'Rinse' && criterion.criterion_label === 'Detergent Residue' && detergentRinseLimit != null) {
                limit = Number(detergentRinseLimit.toFixed(4)); unit = 'ppm';
              }
              if (limit === null) return criterion;
              updatedCount += 1;
              return {
                ...criterion, limit_value: limit, limit_unit: unit,
                notes: 'Auto-fetched from Acceptance Criteria', override_justification: '',
                source_criteria_id: freshAc.id,
                source_master_updated_at: freshAc.updated_at,
                source_synced_at: syncedAt
              };
            })
          };
        });
      setForm((prev) => ({ ...prev, locations: refreshedLocations }));
      setAcRecord(freshAc);
      toast.success(`${updatedCount} acceptance value${updatedCount === 1 ? '' : 's'} refreshed from master`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to refresh Acceptance Criteria');
    }
  };

  const removeCriterion = (locationIndex, criterionIndex) => {
    setForm((prev) => ({
      ...prev,
      locations: prev.locations.map((location, idx) => idx !== locationIndex ? location : ({
        ...location,
        acceptance_criteria: (location.acceptance_criteria || []).filter((_, cIdx) => cIdx !== criterionIndex)
      }))
    }));
  };

  // ── Acceptance-criterion modal (Add / Edit) ─────────────────────────────────
  // Data entry for a criterion now happens in a modal rather than inline table
  // inputs — the table just displays the saved values plus Edit/Remove actions.
  const openAddCriterionModal = (groupEntries) => {
    const first = groupEntries[0];
    setCriterionModal({
      mode: 'add',
      groupEntries,
      locationIndex: first.index,
      criterionIndex: null,
      draft: createCriterion({ sample_medium: first.location.sample_type || '' })
    });
  };

  const openEditCriterionModal = (locationIndex, criterionIndex, criterion) => {
    setCriterionModal({
      mode: 'edit', groupEntries: null, locationIndex, criterionIndex, draft: { ...criterion },
      // Only a criterion that was actually auto-fetched from the Acceptance Criteria/MACO master
      // has a "default" to change away from — typed manually from the start, there's no default
      // to justify a departure from.
      defaultLimitValue: criterion.source_criteria_id ? criterion.limit_value : null,
    });
  };

  const closeCriterionModal = () => setCriterionModal(null);

  const updateCriterionModalField = (field, value) => {
    setCriterionModal((prev) => {
      if (!prev) return prev;
      const next = { ...prev.draft, [field]: value };
      if (field === 'limit_type' && value === 'Visual') {
        next.limit_unit = 'Pass/Fail';
        next.limit_value = prev.draft.limit_value === 'Pass' || prev.draft.limit_value === 'Fail' ? prev.draft.limit_value : 'Pass';
      } else if (field === 'limit_type' && prev.draft.limit_type === 'Visual' && value !== 'Visual') {
        next.limit_unit = '';
        next.limit_value = '';
      }
      return { ...prev, draft: next };
    });
  };

  // Switches which ticked sample-type entry (within the current location group) a new
  // criterion will be attached to — only relevant in 'add' mode with multiple types ticked.
  const changeCriterionModalSampleType = (sampleType) => {
    setCriterionModal((prev) => {
      if (!prev || prev.mode !== 'add') return prev;
      const match = prev.groupEntries.find(({ location }) => location.sample_type === sampleType);
      if (!match) return prev;
      return { ...prev, locationIndex: match.index, draft: { ...prev.draft, sample_medium: sampleType } };
    });
  };

  const saveCriterionModal = () => {
    if (!criterionModal) return;
    if (!criterionModal.draft.criterion_label?.trim()) { toast.error('Criterion label is required'); return; }
    const { mode, locationIndex, criterionIndex, draft, defaultLimitValue } = criterionModal;
    const changedFromDefault = defaultLimitValue !== null && defaultLimitValue !== undefined
      && String(draft.limit_value ?? '') !== String(defaultLimitValue ?? '');
    if (changedFromDefault && !draft.override_justification?.trim()) {
      toast.error('A justification is required to change this limit away from the Acceptance Criteria / MACO default');
      return;
    }
    if (!changedFromDefault) draft.override_justification = '';
    setForm((prev) => ({
      ...prev,
      locations: prev.locations.map((location, idx) => {
        if (idx !== locationIndex) return location;
        if (mode === 'add') {
          return { ...location, acceptance_criteria: [...(location.acceptance_criteria || []), draft] };
        }
        return {
          ...location,
          acceptance_criteria: (location.acceptance_criteria || []).map((c, cIdx) => cIdx === criterionIndex ? draft : c)
        };
      })
    }));
    closeCriterionModal();
  };

  const exportReport = () => {
    const wb = XLSX.utils.book_new();

    const summarySheet = XLSX.utils.json_to_sheet([
      { Field: 'Plan Code', Value: form.plan_code },
      { Field: 'Study Type', Value: form.study_type },
      { Field: 'Train', Value: selectedTrain ? `${selectedTrain.train_code || ''} ${selectedTrain.train_name || ''}`.trim() : '' },
      { Field: 'Previous API', Value: previousApi?.api_name || '' },
      { Field: 'Governing MACO (mg)', Value: form.governing_maco },
      { Field: 'Total Surface Area (cm2)', Value: form.total_surface_area },
      { Field: 'Effective Area (cm2)', Value: form.effective_surface_area },
      { Field: 'MAR (mcg/cm2)', Value: form.mar_mcg_cm2 },
      { Field: 'Swab Concentration (ppm)', Value: form.swab_concentration_ppm },
      { Field: 'Least Rinse Concentration (ppm)', Value: form.least_rinse_concentration_ppm },
      { Field: 'Remarks', Value: form.remarks }
    ]);

    const locationSheet = XLSX.utils.json_to_sheet(
      form.locations.flatMap((location) => (location.acceptance_criteria || []).map((criterion) => ({
        'Location Code': location.location_code,
        'Location Name': location.location_name,
        'Location Type': location.location_type,
        'Surface Type': location.surface_type,
        'Sample Type': location.sample_type,
        'No. of Locations': location.no_of_locations,
        'Selection Rationale': location.rationale,
        'Acceptance Criteria Ref': criterion.acceptance_criteria_ref || location.acceptance_criteria_ref,
        'Criterion Label': criterion.criterion_label,
        'Limit Type': criterion.limit_type,
        'Limit Value': criterion.limit_value,
        'Limit Unit': criterion.limit_unit,
        'Recovery %': criterion.recovery_percent || location.recovery_percent,
        'Include Recovery In Report': criterion.include_recovery_in_report ? 'Yes' : 'No'
      })))
    );

    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
    XLSX.utils.book_append_sheet(wb, locationSheet, 'Locations');
    XLSX.writeFile(wb, `${form.plan_code || 'sampling-plan'}.xlsx`);
  };

  // ── Export PDF ─────────────────────────────────────────────────────────────
  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Sampling Plan Report', pageW / 2, 36, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Plan: ${form.plan_code || '—'} | Study Type: ${form.study_type} | Train: ${selectedTrain ? `${selectedTrain.train_code || ''} ${selectedTrain.train_name || ''}`.trim() : '—'} | Generated: ${new Date().toLocaleString()}`,
      pageW / 2, 52, { align: 'center' }
    );

    doc.autoTable({
      head: [['Field', 'Value']],
      body: [
        ['Previous API', previousApi?.api_name || '—'],
        ['Governing MACO (mg)', form.governing_maco || '—'],
        ['Total Surface Area (cm2)', form.total_surface_area || '—'],
        ['Effective Area (cm2)', form.effective_surface_area || '—'],
        ['MAR (mcg/cm2)', form.mar_mcg_cm2 || '—'],
        ['Swab Concentration (ppm)', form.swab_concentration_ppm || '—'],
        ['Least Rinse Concentration (ppm)', form.least_rinse_concentration_ppm || '—'],
      ],
      startY: 64,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 160, fontStyle: 'bold' } },
      margin: { left: 24, right: 24 },
    });

    const tableRows = form.locations.flatMap((location) => (location.acceptance_criteria || []).map((criterion) => ([
      `${location.location_code || ''} - ${location.location_name || ''}`.trim(),
      location.sample_type + (location.metadata?.interval_label ? ` (${location.metadata.interval_label})` : ''),
      criterion.criterion_label || '—',
      criterion.limit_value ?? '—',
      criterion.limit_unit || '—',
      criterion.notes === 'Auto-fetched from Acceptance Criteria' ? 'Auto-fetched' : 'Manual',
    ])));

    doc.autoTable({
      head: [['Location', 'Sample Type', 'Criterion', 'Limit', 'Unit', 'Source']],
      body: tableRows,
      startY: doc.lastAutoTable.finalY + 20,
      theme: 'grid',
      headStyles: { fillColor: [16, 185, 129], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 7.5 },
      alternateRowStyles: { fillColor: [245, 250, 245] },
      columnStyles: { 0: { cellWidth: 150 } },
      margin: { left: 24, right: 24 },
    });

    stampPdfFooter(doc, 'sampling_plan');
    doc.save(`${form.plan_code || 'sampling-plan'}_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('PDF exported');
  };

  const savePlan = async () => {
    if (!form.plan_code.trim()) {
      toast.error('Plan code is required');
      return;
    }
    if (!form.train_id) {
      toast.error('Equipment train is required');
      return;
    }
    if (['DEHT', 'CEHT'].includes(form.study_type) && !(form.hold_time_intervals || []).some((interval) => String(interval || '').trim())) {
      toast.error(`${form.study_type} requires at least one hold day or sampling interval`);
      return;
    }
    if (form.plan_id && initialPlanRef.current === JSON.stringify(form)) {
      toast.info('No changes were made — nothing to save.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        train_id: form.train_id || null,
        equipment_id: form.equipment_id || null,
        previous_api_id: form.previous_api_id || null,
        next_product_id: form.next_product_id || null,
        locations: form.locations.map((location) => ({
          ...location,
          no_of_locations: Math.max(1, Number(location.no_of_locations) || 1),
          acceptance_criteria: (location.acceptance_criteria || []).map((criterion) => ({
            ...criterion
          }))
        }))
      };

      if (form.plan_id) {
        await samplingPlansAPI.update(form.plan_id, payload);
        toast.success('Sampling plan updated');
      } else {
        await samplingPlansAPI.create(payload);
        toast.success('Sampling plan created');
      }

      const refreshed = await samplingPlansAPI.getAll();
      const refreshedPlans = getRows(refreshed);
      setPlans(refreshedPlans);

      const matching = refreshedPlans.find((plan) => plan.plan_code === form.plan_code) || refreshedPlans[0];
      if (matching) handlePlanSelect(String(matching.plan_id));
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save sampling plan');
    } finally {
      setSaving(false);
    }
  };

  // ── E-sign ─────────────────────────────────────────────────────────────────
  // Draft saves (savePlan above) stay password-free — only the approval-workflow
  // transitions (submit/approve/return-to-draft/delete) require an e-sign.
  const openESign = (mode) => {
    if (mode === 'returnToDraft' && status === 'Approved' && !pendingChangeRef) {
      setChangeRefGate(true);
      return;
    }
    setESignMode(mode); setESignData({ password: '', comment: '', decision: 'approve' }); setShowESign(true);
  };
  const closeESign = () => { setShowESign(false); setESigning(false); };

  const confirmChangeReference = async (data) => {
    setChangeRefBusy(true);
    try {
      setPendingChangeRef(data);
      setChangeRefGate(false);
      setESignMode('returnToDraft'); setESignData({ password: '', comment: '', decision: 'approve' }); setShowESign(true);
    } finally {
      setChangeRefBusy(false);
    }
  };

  const handleConfirmESign = async () => {
    if (!eSignData.password) return toast.error('Password required');
    if (eSignMode === 'returnToDraft' && !eSignData.comment.trim()) return toast.error('Comment required');
    const planId = form.plan_id;
    setESigning(true);
    try {
      if (eSignMode === 'submit') {
        const res = await samplingPlansAPI.submit(planId, { password: eSignData.password });
        setForm((prev) => ({ ...prev, ...res?.data?.data }));
        toast.success('Submitted for approval');
        if (res?.data?.warning) toast.warning(res.data.warning, { autoClose: 10000 });
      } else if (eSignMode === 'returnToDraft') {
        const payload = { comment: eSignData.comment, password: eSignData.password };
        if (status === 'Approved' && pendingChangeRef) {
          payload.change_reference_type = pendingChangeRef.reference_type;
          payload.change_reference_number = pendingChangeRef.reference_number;
          payload.change_reference_files = pendingChangeRef.reference_files || [];
        }
        const res = await samplingPlansAPI.returnToDraft(planId, payload);
        setForm((prev) => ({ ...prev, ...res?.data?.data }));
        setPendingChangeRef(null);
        toast.success('Returned to Draft — you can now edit');
      } else if (eSignMode === 'delete') {
        await samplingPlansAPI.delete(planId, { password: eSignData.password });
        setForm(createEmptyPlan());
        setViewMode('list');
        toast.success('Sampling plan deactivated');
      } else {
        const res = await samplingPlansAPI.approve(planId, {
          stage: eSignMode, decision: eSignData.decision, comment: eSignData.comment, password: eSignData.password,
        });
        setForm((prev) => ({ ...prev, ...res?.data?.data }));
        const label = eSignData.decision === 'approve' ? 'Approved' : eSignData.decision === 'return' ? 'Returned' : 'Rejected';
        toast.success(`${eSignMode === 'product' ? 'Product' : 'QA'} review: ${label}`);
      }
      closeESign();
      const refreshed = await samplingPlansAPI.getAll();
      setPlans(getRows(refreshed));
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Action failed');
    } finally {
      setESigning(false);
    }
  };

  // Carries the study type a dashboard chart was drilled into (the "Sampling Plans"
  // bar chart groups by study_type) through to this list's own filter.
  useEffect(() => {
    if (!location.state?.filterStudyType) return;
    setPlanTypeFilter(location.state.filterStudyType);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const planListRows = plans
    .filter((plan) => matchesGroup(approvalStatusGroups, planStatusCard, plan))
    .filter((plan) => !planTypeFilter || plan.study_type === planTypeFilter)
    .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
  const planListTotalPages = Math.max(1, Math.ceil(planListRows.length / planListPageSize));
  useEffect(() => { setPlanListPage(1); }, [planStatusCard, planTypeFilter, planListPageSize]);
  useEffect(() => { if (planListPage > planListTotalPages) setPlanListPage(planListTotalPages); }, [planListPage, planListTotalPages]);
  const paginatedPlanListRows = planListRows.slice((planListPage - 1) * planListPageSize, planListPage * planListPageSize);

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  const eSignTitle = {
    submit:         'Submit for Approval — Electronic Signature',
    product:        `${earlierLabel} — Electronic Signature`,
    qa:             `${finalLabel} — Electronic Signature`,
    returnToDraft:  'Return to Draft — Electronic Signature',
    delete:         'Deactivate Record — Electronic Signature',
  }[eSignMode];

  const openNewPlanModal = () => {
    setNewPlanDraft({ study_type: 'CV', train_id: trains[0] ? String(trains[0].train_id) : '', source_plan_id: '' });
    setShowNewPlanModal(true);
  };

  const openCopyPlanModal = (plan) => {
    setNewPlanDraft({
      study_type: plan?.study_type || form.study_type || 'CV',
      train_id: String(plan?.train_id || form.train_id || trains[0]?.train_id || ''),
      source_plan_id: plan?.plan_id ? String(plan.plan_id) : ''
    });
    setShowNewPlanModal(true);
  };

  const createPlanFromModal = async () => {
    if (!newPlanDraft.train_id) { toast.error('Equipment train is required'); return; }
    setCreatingPlan(true);
    try {
      const res = await settingsAPI.nextSequence('plan');
      const next = res?.data?.data?.next;
      const planCode = `${numberingConfig.planPrefix}-${String(next).padStart(numberingConfig.planPadding, '0')}-${newPlanDraft.study_type}`;
      const source = plans.find((plan) => String(plan.plan_id) === String(newPlanDraft.source_plan_id));
      let copiedLocations = [];
      if (source) {
        for (const location of source.locations || []) {
          let sampleId = '';
          try {
            const sampleRes = await settingsAPI.nextSequence('sample');
            const sampleNext = sampleRes?.data?.data?.next;
            const suffix = { Swab: 'SWB', Rinse: 'RNS', Micro: 'MIC', Visual: 'VIS' }[location.sample_type] || 'SMP';
            sampleId = `${numberingConfig.samplePrefix}-${String(sampleNext).padStart(numberingConfig.samplePadding, '0')}-${suffix}`;
          } catch { /* A missing ID can be generated later from the location editor. */ }
          copiedLocations.push({
            ...createLocation(), ...location,
            location_id: null,
            plan_id: null,
            metadata: { ...(location.metadata || {}), sample_id: sampleId }
          });
        }
      }
      setForm({
        ...createEmptyPlan(), ...(source || {}),
        plan_id: null, plan_code: planCode, status: 'Draft',
        study_type: newPlanDraft.study_type, train_id: newPlanDraft.train_id,
        saved_by_name: null, saved_at: null, product_approved_by_name: null,
        product_approved_at: null, qa_approved_by_name: null, qa_approved_at: null,
        predecessor_plan_id: '', locations: copiedLocations
      });
      autoPopulateNewPlanRef.current = !source;
      setShowNewPlanModal(false);
      setViewMode('builder');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to generate plan code');
    } finally {
      setCreatingPlan(false);
    }
  };

  const copySourcePlans = plans.filter((plan) => String(plan.train_id) === String(newPlanDraft.train_id) && plan.study_type === newPlanDraft.study_type);
  const newPlanModal = showNewPlanModal && (
    <Modal isOpen size="sm" title="New Sampling Plan" onClose={() => setShowNewPlanModal(false)}>
          <div className="form-group">
            <label className="form-label">Study Type</label>
            <select className="form-control" value={newPlanDraft.study_type} onChange={(e) => setNewPlanDraft((prev) => ({ ...prev, study_type: e.target.value, source_plan_id: '' }))}>
              <option value="CV">CV</option>
              {moduleOn('ccv') && <option value="CCV">CCV</option>}
              {moduleOn('hold_time') && <option value="CEHT">CEHT</option>}
              {moduleOn('hold_time') && <option value="DEHT">DEHT</option>}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Equipment Train *</label>
            <select className="form-control" value={newPlanDraft.train_id} onChange={(e) => setNewPlanDraft((prev) => ({ ...prev, train_id: e.target.value, source_plan_id: '' }))}>
              <option value="">Select train</option>
              {trains.map((train) => (
                <option key={train.train_id} value={train.train_id}>{train.train_code} - {train.train_name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Start from existing plan <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span></label>
            <select className="form-control" value={newPlanDraft.source_plan_id || ''}
              onChange={(e) => setNewPlanDraft((prev) => ({ ...prev, source_plan_id: e.target.value }))}>
              <option value="">Auto-build from train and Acceptance Criteria</option>
              {copySourcePlans.map((plan) => <option key={plan.plan_id} value={plan.plan_id}>{plan.plan_code} · {plan.status || 'Draft'}</option>)}
            </select>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {copySourcePlans.length ? 'Copies locations and criteria into a new editable draft; results and approvals are not copied.' : 'Matching plans on this train and study type will appear here.'}
            </div>
          </div>
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', margin: 0 }}>
            Plan Code is auto-numbered on creation (prefix/digits configurable in Settings).
          </p>
        <ModalFooter>
          <button className="btn btn-outline" onClick={() => setShowNewPlanModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={createPlanFromModal} disabled={creatingPlan || !newPlanDraft.train_id}>
            {creatingPlan ? 'Creating…' : 'Create Plan'}
          </button>
        </ModalFooter>
    </Modal>
  );

  if (viewMode === 'list') {
    return (
      <div>
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <h1 className="card-title">Sampling Plans</h1>
              <p style={{ margin: '6px 0 0', color: 'var(--text-secondary)' }}>Open an existing plan, or create a new one for CV, CCV, CEHT or DEHT.</p>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {planTypeFilter && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '999px', background: 'var(--info-bg)', color: 'var(--info-text)', fontSize: 'var(--fs-sm)' }}>
                  Study Type: {planTypeFilter}
                  <button type="button" onClick={() => setPlanTypeFilter('')} title="Clear filter" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700, lineHeight: 1 }}>&times;</button>
                </span>
              )}
              <button className="btn btn-primary" type="button" onClick={openNewPlanModal}><FaPlus style={{ marginRight: '4px' }} />New Plan</button>
            </div>
          </div>
          <div style={{ padding: '12px 16px 0' }}>
            <FilterStatCards groups={approvalStatusGroups} records={plans} value={planStatusCard} onChange={setPlanStatusCard} totalTitle="Total Plans" totalIcon={FaClipboardList} />
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Plan Code</th>
                  <th>Study Type</th>
                  <th>Train</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {planListRows.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted">No sampling plans yet — click "New Plan" to create one.</td></tr>
                ) : paginatedPlanListRows.map((plan) => (
                  <tr key={plan.plan_id} style={{ cursor: 'pointer' }} onClick={() => handlePlanSelect(String(plan.plan_id))}>
                    <td>{plan.plan_code}</td>
                    <td>{plan.study_type}</td>
                    <td>
                      {plan.train_id ? (
                        <Link to="/equipment-trains" state={{ openViewTrainId: plan.train_id }} className="record-link" title="View train" onClick={(e) => e.stopPropagation()}>
                          {plan.train_name || plan.train_code}
                        </Link>
                      ) : (plan.train_name || plan.train_code || '—')}
                    </td>
                    <td>{plan.status ? <StatusBadge status={plan.status} /> : <StatusBadge status="Draft" />}</td>
                    <td>{plan.updated_at ? new Date(plan.updated_at).toLocaleString() : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="btn btn-outline btn-sm" type="button" onClick={(e) => { e.stopPropagation(); handlePlanSelect(String(plan.plan_id)); }}>Open</button>
                        <button className="btn btn-outline btn-sm" type="button" onClick={(e) => { e.stopPropagation(); openCopyPlanModal(plan); }}>Copy</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {planListRows.length > 0 && (
            <TablePagination total={planListRows.length} page={planListPage} pageSize={planListPageSize} onPageChange={setPlanListPage} onPageSizeChange={(n) => { setPlanListPageSize(n); setPlanListPage(1); }} itemLabel="plans" />
          )}
        </div>

        {newPlanModal}
      </div>
    );
  }

  return (
    <div className="sampling-plan-page">
      <div className="sampling-plan-builder-header" style={{ marginBottom: '20px' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: 0 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h1 className="card-title">Sampling Plan Builder</h1>
              {form.plan_id && status && <StatusBadge status={status} />}
            </div>
            <p className="sampling-plan-page-intro">Set the study scope, choose where to sample, then save or submit the plan.</p>
          </div>
          <div className="sampling-plan-header-actions">
            <button className="btn btn-outline btn-sm" type="button" onClick={() => setViewMode('list')}>Back to Plans</button>
            <button className="btn btn-primary" type="button" onClick={savePlan} disabled={saving || !canEdit} title={!canEdit ? 'Return this plan to Draft before editing' : undefined}>
              {saving ? 'Saving...' : form.plan_id ? 'Update Plan' : 'Save Plan'}
            </button>
            <ActionsMenu
              title="More actions"
              items={[
                { label: 'AI Draft Report', icon: <FaRobot />, module: 'ai_assistant', onClick: () => ai.run('AI Draft Report — Sampling Plans', () => aiAPI.draftReport({ reportType: 'sampling_plans' }), 'report') },
                { label: 'New Plan', onClick: openNewPlanModal },
                { label: 'Copy This Plan', icon: <FaPlus />, show: !!form.plan_id, onClick: () => openCopyPlanModal(plans.find((plan) => String(plan.plan_id) === String(form.plan_id)) || form) },
                { label: 'Export Excel', icon: <FaFileExcel />, onClick: exportReport },
                { label: 'Export PDF', icon: <FaFilePdf />, onClick: exportPdf },
                { divider: true },
                { label: 'Submit for Approval', icon: <FaFileUpload />, show: approvalRequired && canEdit && !!form.plan_id, onClick: () => openESign('submit') },
                { label: earlierLabel, icon: <FaCheck />, show: approvalRequired && status === 'Pending Approval' && canProductApprove, onClick: () => openESign('product') },
                { label: finalLabel, icon: <FaCheck />, show: approvalRequired && status === 'Pending QA Approval' && canQaApprove, onClick: () => openESign('qa') },
                { label: 'Version History', icon: <FaEye />, show: !!form.plan_id, onClick: () => setHistoryModalOpen(true) },
                { label: 'Edit', icon: <FaEdit />, show: approvalRequired && isAdmin && status && status !== 'Draft', onClick: () => openESign('returnToDraft') },
                { label: 'Deactivate', icon: <FaTrashAlt />, danger: true, show: isAdmin && !!form.plan_id, onClick: () => openESign('delete') },
              ]}
            />
          </div>
        </div>
        {form.plan_id && (form.saved_by_name || form.product_approved_by_name || form.qa_approved_by_name) && (
          <div style={{ padding: '7px 20px', borderTop: '1px solid var(--border-color)', display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
            {form.saved_by_name && <span><strong>Saved:</strong> {form.saved_by_name} on {new Date(form.saved_at).toLocaleString()}</span>}
            {form.product_approved_by_name && <span><strong>Product:</strong> {form.product_approval_decision} by {form.product_approved_by_name} on {new Date(form.product_approved_at).toLocaleString()}</span>}
            {form.qa_approved_by_name && <span><strong>QA:</strong> {form.qa_approval_decision} by {form.qa_approved_by_name} on {new Date(form.qa_approved_at).toLocaleString()}</span>}
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>1. Study Scope</h2>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>Choose one train per plan. Worst-case API and limits are sourced from the approved masters and governing Acceptance Criteria.</p>
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Plan Code</label>
            <input className="form-control" value={form.plan_code} disabled title="Auto-numbered on creation — see Settings to change the prefix/digits" />
          </div>
          <div className="form-group">
            <label className="form-label">Study Type</label>
            <select className="form-control" value={form.study_type} onChange={(e) => handleFormField('study_type', e.target.value)}>
              <option value="CV">CV</option>
              {moduleOn('ccv') && <option value="CCV">CCV</option>}
              {moduleOn('hold_time') && <option value="CEHT">CEHT</option>}
              {moduleOn('hold_time') && <option value="DEHT">DEHT</option>}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Equipment Train</label>
            <select className="form-control" value={form.train_id} onChange={(e) => handleFormField('train_id', e.target.value)}>
              <option value="">Select train</option>
              {trains.map((train) => (
                <option key={train.train_id} value={train.train_id}>{train.train_code} - {train.train_name}</option>
              ))}
            </select>
          </div>
          {isHoldTimeStudy && (
            <div className="form-group">
              <label className="form-label">No. of Batches</label>
              <input className="form-control" type="number" min="1" value={form.no_of_batches ?? 3}
                onChange={(e) => handleFormField('no_of_batches', Math.max(1, Number(e.target.value) || 1))}
                title="Typically 3 batches; can be reduced to 1 per client policy" />
            </div>
          )}
          {predecessorStudyType && (
            <div className="form-group">
              <label className="form-label">Predecessor {predecessorStudyType} Plan</label>
              <select className="form-control" value={form.predecessor_plan_id || ''} onChange={(e) => handleFormField('predecessor_plan_id', e.target.value)}>
                <option value="">— None linked —</option>
                {predecessorCandidates.map((plan) => (
                  <option key={plan.plan_id} value={plan.plan_id}>{plan.plan_code} ({plan.status || 'Draft'})</option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Previous API</label>
            <select className="form-control" value={form.previous_api_id} onChange={(e) => handleFormField('previous_api_id', e.target.value)}>
              <option value="">Select API</option>
              {apis.map((api) => (
                <option key={api.api_id} value={api.api_id}>{api.api_name}</option>
              ))}
            </select>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {liveWorstCase?.approved_worst_case ? 'Defaults to MACO\'s governing (Approved) worst-case pair — override if needed.'
                : liveWorstCase?.worst_case ? 'Defaults to the current best draft pick (not yet Approved) — override if needed.'
                : 'Defaults to the worst-case API for this train — override if needed.'}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Next Product</label>
            <select className="form-control" value={form.next_product_id || ''} onChange={(e) => handleFormField('next_product_id', e.target.value)}>
              <option value="">Select product</option>
              {productsOnSelectedTrain.map((product) => (
                <option key={product.product_id} value={product.product_id}>{product.product_name}</option>
              ))}
            </select>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', marginTop: '4px' }}>The next product scheduled on this train after cleaning — paired with Previous API for the governing limit.</div>
          </div>
          <div className="form-group">
            <label className="form-label">Swab Area / Diluent</label>
            <input className="form-control" disabled
              value={acRecord ? `${acRecord.swab_area_cm2 ?? '—'} cm² / ${acRecord.swab_diluent_ml ?? '—'} mL` : 'Set in Acceptance Criteria'}
              title="From Acceptance Criteria — no re-entry needed" />
          </div>
        </div>

        {linkedTrainCoverage.length > 1 && (
          <div style={{ margin: '0 20px 16px', padding: '12px 14px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-color)' }}>
            <strong style={{ fontSize: 'var(--fs-md)' }}>Train Coverage</strong>
            <span style={{ marginLeft: '8px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{productsOnSelectedTrain.length} linked product(s); each train is planned separately</span>
            <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
              {linkedTrainCoverage.map(({ train, plan }) => (
                <div key={train.train_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: 'var(--fs-sm)' }}>
                  <span>{train.train_code} - {train.train_name}{String(train.train_id) === String(form.train_id) ? ' (this plan)' : ''}</span>
                  {plan ? <span style={{ color: 'var(--success-text)' }}>{plan.plan_code} · {plan.status || 'Draft'}</span> : String(train.train_id) === String(form.train_id) ? <span>Current train scope</span> : (
                    <button className="btn btn-outline btn-sm" type="button" disabled={!form.plan_id} title={!form.plan_id ? 'Save this plan before creating its follow-up plan' : ''} onClick={() => {
                      setNewPlanDraft({ study_type: form.study_type, train_id: String(train.train_id) });
                      setShowNewPlanModal(true);
                    }}>Create Follow-up Plan</button>
                  )}
                </div>
              ))}
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>Each linked train gets its own equipment-part list and train-specific surface-area/MACO calculations.</p>
          </div>
        )}

        {predecessorStudyType && (
          form.predecessor_plan_id && predecessorPlan ? (
            predecessorPlan.status !== 'Approved' && (
              <div style={{ margin: '0 20px 16px', padding: '10px 14px', borderRadius: '8px', background: 'color-mix(in srgb, var(--warning-color) 12%, var(--card-bg))', border: '1px solid var(--warning-color)', fontSize: 'var(--fs-md)' }}>
                ⚠ Linked {predecessorStudyType} plan <strong>{predecessorPlan.plan_code}</strong> is not yet Approved (status: {predecessorPlan.status || 'Draft'}).
                Per protocol, {predecessorStudyType} should complete{predecessorStudyType === 'DEHT' ? ', then Cleaning Validation,' : ''} before {form.study_type} sampling begins.
              </div>
            )
          ) : (
            <div style={{ margin: '0 20px 16px', padding: '10px 14px', borderRadius: '8px', background: 'color-mix(in srgb, var(--warning-color) 12%, var(--card-bg))', border: '1px solid var(--warning-color)', fontSize: 'var(--fs-md)' }}>
              No {predecessorStudyType} plan linked for this train yet. Per protocol, {predecessorStudyType} should complete{predecessorStudyType === 'DEHT' ? ', then Cleaning Validation,' : ''} before {form.study_type} sampling begins — link it above once available.
            </div>
          )
        )}

        {isHoldTimeStudy && form.established_hold_time && (
          <div style={{ margin: '0 20px 16px', padding: '10px 14px', borderRadius: '8px', background: 'var(--info-bg)', border: '1px solid var(--info-border)', color: 'var(--info-text)', fontSize: 'var(--fs-sm)' }}>
            Established hold time: <strong>{form.established_hold_time}</strong> — {form.established_hold_time_basis}
          </div>
        )}

        {isHoldTimeStudy && (
          <div style={{ margin: '0 20px 16px', padding: '12px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-color)' }}>
            <label className="form-label">{form.study_type === 'DEHT' ? 'Dirty Equipment Hold Days' : 'Clean Equipment Hold Days'}</label>
            <p style={{ margin: '0 0 8px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
              Add at least one day or interval. Each location is sampled at every listed point{form.study_type === 'DEHT' ? ', including Degradant on API/product-contact surfaces.' : ' after cleaning.'}
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {(form.hold_time_intervals || []).map((label) => (
                <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '999px', padding: '3px 6px 3px 12px', fontSize: 'var(--fs-sm)' }}>
                  {label}
                  <button type="button" onClick={() => removeHoldTimeInterval(label)} title="Remove interval"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger-color)', fontWeight: 700, padding: '0 4px' }}>×</button>
                </span>
              ))}
              {(form.hold_time_intervals || []).length === 0 && (
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--warning-text)' }}>Required: add at least one hold day before saving this plan.</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input className="form-control" style={{ maxWidth: '220px' }} placeholder="e.g. Day 3" value={newIntervalLabel}
                onChange={(e) => setNewIntervalLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHoldTimeInterval(); } }} />
              <button type="button" className="btn btn-outline btn-sm" onClick={addHoldTimeInterval}>+ Add Interval</button>
            </div>
          </div>
        )}
      </div>

      <details className="card sampling-plan-advanced-card">
        <summary className="sampling-plan-advanced-summary">
          <span><strong>Calculated limits & train coverage</strong><small>Auto-derived values and surface area details</small></span>
          <span className="sampling-plan-summary-hint">{selectedTrain ? selectedTrain.train_code : 'Select a train'} · {trainMembers.length} equipment</span>
        </summary>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>2. Automatic Limits And Train Coverage</h2>
            <p style={{ margin: '3px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>Limits come from saved Acceptance Criteria; rinse concentration is the lowest calculated value among this train's equipment members.</p>
            <p style={{ margin: '3px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>Available in train: {trainMembers.length} equipment. In this plan: {new Set(form.locations.filter((location) => location.equipment_part_id).map((location) => location.equipment_part_id)).size} parts / {new Set(form.locations.filter((location) => location.equipment_id).map((location) => location.equipment_id)).size} equipment.</p>
          </div>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Train</td><td>{selectedTrain ? `${selectedTrain.train_code || ''} - ${selectedTrain.train_name || ''}` : '-'}</td></tr>
              <tr><td>Governing MACO (mg)</td><td>{fmt(form.governing_maco, 6)}</td></tr>
              <tr><td>Total Surface Area (cm2)</td><td>{fmt(form.total_surface_area, 2)}</td></tr>
              <tr><td>Effective Surface Area (cm2)</td><td>{fmt(form.effective_surface_area, 2)}</td></tr>
              <tr><td>MAR (mcg/cm2)</td><td>{fmt(form.mar_mcg_cm2, 6)}</td></tr>
              <tr><td>Swab Concentration (ppm)</td><td>{fmt(form.swab_concentration_ppm, 6)}</td></tr>
              <tr><td>Least Rinse Concentration (ppm)</td><td>{fmt(form.least_rinse_concentration_ppm, 6)}</td></tr>
            </tbody>
          </table>
        </div>
      </details>

      {form.plan_id && form.locations.length > 0 && (
        <details className="card sampling-plan-advanced-card">
          <summary className="sampling-plan-advanced-summary">
            <span><strong>Batch completion</strong><small>QA-approved result progress</small></span>
            <span className="sampling-plan-summary-hint">{form.locations.length} sampling entries · {MIN_BATCHES_REQUIRED} batches required</span>
          </summary>
          <div className="card-header">
            <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Batch Completion</h2>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
              Minimum {MIN_BATCHES_REQUIRED} consecutive batches with QA-Approved results required per location before this study is complete.
            </p>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Sample Type</th>
                  <th>Batches Completed</th>
                </tr>
              </thead>
              <tbody>
                {form.locations.map((location, idx) => {
                  const completed = location.location_id ? (batchProgressByLocation[String(location.location_id)] || 0) : 0;
                  const complete = completed >= MIN_BATCHES_REQUIRED;
                  return (
                    <tr key={`batch-location-${location.location_id || 'new'}-${idx}`}>
                      <td>{location.location_code} - {location.location_name}</td>
                      <td>{location.sample_type}</td>
                      <td>
                        <span style={{
                          padding: '2px 10px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-xs)',
                          background: complete ? 'var(--success-bg)' : 'var(--warning-bg)',
                          color: complete ? 'var(--success-text)' : 'var(--warning-text)',
                          border: `1px solid ${complete ? 'var(--success-border)' : 'var(--warning-border)'}`
                        }}>
                          {completed} / {MIN_BATCHES_REQUIRED}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>3. Sampling Locations And Acceptance Criteria</h2>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
              Pick equipment (or a sub-part) on the left, then tick which sample types apply. Limits marked "Auto-fetched" came straight from Acceptance Criteria — no re-entry needed.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {acRecord && canEdit && <button className="btn btn-outline btn-sm" type="button" onClick={refreshAllLimitsFromMaster} title="Update applicable visual and residue limits from the latest master">Refresh master limits</button>}
            <button className="btn btn-primary btn-sm" type="button" onClick={() => {
              const generated = buildLocationsFromTrain();
              setForm((prev) => {
                const existing = new Set(prev.locations.map((location) => `${location.equipment_part_id || location.equipment_id || ''}:${location.sample_type || ''}:${location.metadata?.interval_label || ''}`));
                const missing = generated.filter((location) => {
                  const key = `${location.equipment_part_id || location.equipment_id || ''}:${location.sample_type || ''}:${location.metadata?.interval_label || ''}`;
                  if (existing.has(key)) return false;
                  existing.add(key);
                  return true;
                });
                return { ...prev, locations: [...prev.locations, ...missing] };
              });
            }} disabled={!trainMembers.length} title="Create sampling locations for every active equipment part in this train; existing locations are skipped">
              Add All Train Parts ({trainLocationCount})
            </button>
            <button className="btn btn-outline btn-sm" type="button" onClick={addManualLocationGroup}>+ Add Manual Surface</button>
          </div>
        </div>

        <div style={{ margin: '0 16px 12px', padding: '10px 14px', borderRadius: '8px', background: acRecord ? 'color-mix(in srgb, var(--success-color) 8%, var(--card-bg))' : 'color-mix(in srgb, var(--warning-color) 12%, var(--card-bg))', border: `1px solid ${acRecord ? 'var(--success-color)' : 'var(--warning-color)'}`, fontSize: 'var(--fs-sm)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <span><strong>{acRecord ? 'Acceptance Criteria linked' : 'Acceptance Criteria not linked'}</strong> · {acRecord ? 'Residue and visual limits can be pulled into matching sample types.' : 'You can enter limits manually now; save the master criteria to enable smart fill.'}</span>
          {acRecord && <span style={{ color: 'var(--text-secondary)' }}>Master updated {acRecord.updated_at ? new Date(acRecord.updated_at).toLocaleDateString() : '—'}</span>}
        </div>

        <div className="sampling-plan-location-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)', gap: '16px', padding: '0 16px 16px', alignItems: 'start' }}>
          {/* ── LEFT: equipment / sub-part tree ─────────────────────────────── */}
          <div className="sampling-plan-equipment-tree" style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '10px', maxHeight: '640px', overflowY: 'auto', background: 'var(--card-bg)' }}>
            {equipmentTree.length === 0 && manualNodes.length === 0 && (
              <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', padding: '8px' }}>Select an Equipment Train above to see its equipment and sub-parts here.</div>
            )}
            {equipmentTree.map((equipNode) => {
              const expanded = !!expandedEquipment[equipNode.key];
              const equipGroupCount = groupsForNode(equipNode.key).length;
              return (
                <div key={equipNode.key} style={{ marginBottom: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {equipNode.parts.length > 0 ? (
                      <button type="button" onClick={() => setExpandedEquipment((prev) => ({ ...prev, [equipNode.key]: !prev[equipNode.key] }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', width: '20px', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
                        {expanded ? '▾' : '▸'}
                      </button>
                    ) : <span style={{ width: '20px', display: 'inline-block' }} />}
                    <button type="button" onClick={() => {
                      if (equipNode.parts.length) {
                        const firstPart = equipNode.parts[0];
                        setExpandedEquipment((prev) => ({ ...prev, [equipNode.key]: true }));
                        setSelectedNode(firstPart);
                        const groups = groupsForNode(firstPart.key);
                        if (groups.length === 0) addLocationGroup(firstPart);
                        else setActiveLocationGroup(`${firstPart.key}-${groups[0]}`);
                      } else {
                        setSelectedNode(equipNode);
                        const groups = groupsForNode(equipNode.key);
                        if (groups.length === 0) addLocationGroup(equipNode);
                        else setActiveLocationGroup(`${equipNode.key}-${groups[0]}`);
                      }
                    }}
                      style={{
                        flex: 1, textAlign: 'left', background: selectedNode?.key === equipNode.key ? 'color-mix(in srgb, var(--primary-color) 12%, var(--card-bg))' : 'none',
                        border: 'none', borderRadius: '6px', padding: '5px 6px', cursor: 'pointer', fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)',
                        display: 'flex', justifyContent: 'space-between', gap: '6px'
                      }}>
                      <span>{equipNode.label}</span>
                      {equipGroupCount > 0 && <span style={{ fontSize: 'var(--fs-xs)', background: 'var(--primary-color)', color: '#fff', borderRadius: '999px', padding: '1px 7px' }}>{equipGroupCount}</span>}
                    </button>
                  </div>
                  {expanded && equipNode.parts.map((part) => {
                    const partGroupCount = groupsForNode(part.key).length;
                    return (
                      <button key={part.key} type="button" onClick={() => {
                        setSelectedNode(part);
                        const groups = groupsForNode(part.key);
                        if (groups.length === 0) addLocationGroup(part);
                        else setActiveLocationGroup(`${part.key}-${groups[0]}`);
                      }}
                        style={{
                          display: 'flex', justifyContent: 'space-between', gap: '6px', width: 'calc(100% - 26px)', marginLeft: '26px',
                          background: selectedNode?.key === part.key ? 'color-mix(in srgb, var(--primary-color) 12%, var(--card-bg))' : 'none',
                          border: 'none', borderRadius: '6px', padding: '4px 6px', cursor: 'pointer', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)'
                        }}>
                        <span>{part.label}</span>
                        {partGroupCount > 0 && <span style={{ fontSize: 'var(--fs-xs)', background: 'var(--success-color)', color: '#fff', borderRadius: '999px', padding: '1px 7px' }}>{partGroupCount}</span>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
            {manualNodes.length > 0 && (
              <>
                <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary)', margin: '10px 0 4px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
                  Additional Locations (not on equipment train)
                </div>
                {manualNodes.map((node) => (
                  <button key={node.key} type="button" onClick={() => {
                    setSelectedNode(node);
                    const groups = groupsForNode(node.key);
                    setActiveLocationGroup(`${node.key}-${groups[0] ?? 0}`);
                  }}
                    style={{
                      display: 'flex', justifyContent: 'space-between', width: '100%',
                      background: selectedNode?.key === node.key ? 'color-mix(in srgb, var(--primary-color) 12%, var(--card-bg))' : 'none',
                      border: 'none', borderRadius: '6px', padding: '5px 6px', cursor: 'pointer', fontSize: 'var(--fs-md)', color: 'var(--text-primary)'
                    }}>
                    {node.label}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* ── RIGHT: selected node's location group(s) ────────────────────── */}
          <div>
            {!selectedNode ? (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>
                Click an equipment, sub-part, or additional location on the left to add or edit its sampling locations.
              </div>
            ) : (
              <>
                <div className="sampling-plan-selected-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <div><div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Selected equipment / surface</div><strong style={{ fontSize: 'var(--fs-lg)' }}>{selectedNode.label}</strong></div>
                  <button className="btn btn-primary btn-sm" type="button" onClick={() => addLocationGroup(selectedNode)}><FaPlus style={{ marginRight: 6 }} />Add sampling location</button>
                </div>

                {groupsForNode(selectedNode.key).map((groupSeq) => {
                  const groupEntries = locationsInGroup(selectedNode.key, groupSeq);
                  const first = groupEntries[0]?.location;
                  if (!first) return null;
                  const tickedTypes = new Set(groupEntries.map(({ location }) => location.sample_type));

                  return (
                    <details key={groupSeq} open={activeLocationGroup === `${selectedNode.key}-${groupSeq}`} className="sampling-plan-location-editor">
                      <summary className="sampling-plan-location-summary">
                        <span>Location {groupSeq + 1}: {first.location_code || first.location_name}</span>
                        <span className="sampling-plan-location-summary-meta">{[...tickedTypes].join(' · ') || 'No sample type'}{first.metadata?.sampling_area_grade ? ` · ${first.metadata.sampling_area_grade}` : ''}</span>
                      </summary>
                      <div className="sampling-plan-location-body">
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 'var(--fs-md)', fontWeight: 700, color: 'var(--text-secondary)' }}>Location {groupSeq + 1}: {first.location_code}</span>
                        <button className="btn btn-danger btn-sm" type="button" onClick={() => removeLocationGroup(selectedNode.key, groupSeq)}>Remove This Location</button>
                      </div>

                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Location ID / Code</label>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <input className="form-control" value={first.location_code || ''} onChange={(e) => handleGroupField(selectedNode.key, groupSeq, 'location_code', e.target.value)} disabled={!!selectedNode.part_id || !!selectedNode.equipment_id} />
                            {!selectedNode.part_id && !selectedNode.equipment_id && (
                              <button type="button" className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap' }}
                                disabled={generatingLocationCode === `${selectedNode.key}-${groupSeq}`}
                                onClick={async () => {
                                  setGeneratingLocationCode(`${selectedNode.key}-${groupSeq}`);
                                  try {
                                    const res = await settingsAPI.nextSequence('location');
                                    const next = res?.data?.data?.next;
                                    handleGroupField(selectedNode.key, groupSeq, 'location_code', `${numberingConfig.locationPrefix}-${String(next).padStart(numberingConfig.locationPadding, '0')}`);
                                  } catch (error) {
                                    toast.error('Failed to generate location code');
                                  } finally {
                                    setGeneratingLocationCode(null);
                                  }
                                }}>
                                {generatingLocationCode === `${selectedNode.key}-${groupSeq}` ? '…' : '+ Generate'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Location Name</label>
                          <input className="form-control" value={first.location_name || ''} onChange={(e) => handleGroupField(selectedNode.key, groupSeq, 'location_name', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Surface Type</label>
                          {selectedNode.part_id ? (
                            <input className="form-control" value={first.surface_type || ''} disabled title="Auto-filled from the equipment part's material of construction" />
                          ) : (
                            <select className="form-control" value={first.surface_type || ''} onChange={(e) => handleGroupField(selectedNode.key, groupSeq, 'surface_type', e.target.value)}>
                              <option value="">— Select —</option>
                              {STANDARD_MATERIALS_OF_CONSTRUCTION.map((m) => <option key={m} value={m}>{m}</option>)}
                              <option value="Other">Other</option>
                            </select>
                          )}
                        </div>
                        <div className="form-group">
                          <label className="form-label">Location Photo / Diagram</label>
                          <input type="file" accept="image/*" className="form-control"
                            onChange={async (e) => {
                              const groupKey = `${selectedNode.key}-${groupSeq}`;
                              setUploadingPhotoKey(groupKey);
                              await handleGroupPhoto(selectedNode.key, groupSeq, e.target.files?.[0]);
                              setUploadingPhotoKey((current) => (current === groupKey ? null : current));
                            }} />
                          {uploadingPhotoKey === `${selectedNode.key}-${groupSeq}` && (
                            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '4px' }}>Uploading…</div>
                          )}
                          {first?.metadata?.photo_url && (
                            <img src={first.metadata.photo_url} alt="Location" style={{ marginTop: '6px', maxHeight: '70px', borderRadius: '6px', border: '1px solid var(--border-color)' }} />
                          )}
                        </div>
                      </div>

                      {isHoldTimeStudy && (
                        <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '8px', background: 'var(--bg-color)', border: '1px solid var(--border-color)' }}>
                          {form.study_type === 'DEHT' && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--fs-md)', cursor: 'pointer', fontWeight: 600, marginBottom: '8px' }}>
                              <input type="checkbox" checked={isApiContactSurface(first)}
                                onChange={(e) => toggleDehtApiContact(selectedNode.key, groupSeq, e.target.checked)} />
                              API / Product Contact Surface (adds Degradant test)
                            </label>
                          )}
                          <label className="form-label" style={{ marginBottom: '4px' }}>Hold-Time Intervals Sampled Here</label>
                          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                            {(form.hold_time_intervals || []).length === 0 && (
                              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>Add intervals above (e.g. Day 3, Day 6, Day 9) to sample this location.</span>
                            )}
                            {(form.hold_time_intervals || []).map((intervalLabel) => {
                              const ticked = groupEntries.some(({ location }) => location.sample_type === 'Micro' && location.metadata?.interval_label === intervalLabel);
                              return (
                                <label key={intervalLabel} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--fs-md)', cursor: 'pointer', fontWeight: ticked ? 700 : 500 }}>
                                  <input type="checkbox" checked={ticked} onChange={() => toggleHoldTimeInterval(selectedNode, groupSeq, intervalLabel)} />
                                  {intervalLabel}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="sampling-plan-input-section" style={{ marginTop: '14px' }}>
                        <label className="form-label">Sample Types {isHoldTimeStudy && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional beyond the intervals above)</span>}</label>
                        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                          {SAMPLE_TYPES.map((type) => (
                            <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--fs-md)', cursor: 'pointer', fontWeight: tickedTypes.has(type) ? 700 : 500 }}>
                              <input type="checkbox" checked={tickedTypes.has(type)} onChange={() => toggleSampleType(selectedNode, groupSeq, type)} />
                              {type}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="form-group" style={{ marginTop: '10px' }}>
                        <label className="form-label">Sampling Area Grade</label>
                        <input className="form-control" value={first.metadata?.sampling_area_grade || ''} onChange={(e) => handleGroupField(selectedNode.key, groupSeq, 'sampling_area_grade', e.target.value)} placeholder="e.g. Grade C (actual sampling/cleaning area)" />
                      </div>

                      <div className="form-group" style={{ marginTop: '10px' }}>
                        <label className="form-label">Rationale Of Selection</label>
                        <textarea className="form-control" rows={2} value={first.rationale || ''} onChange={(e) => handleGroupField(selectedNode.key, groupSeq, 'rationale', e.target.value)} placeholder="Why this location was selected" />
                      </div>

                      {groupEntries.length > 0 && (
                        <details className="sampling-plan-criteria-details">
                          <summary><span>Acceptance Criteria</span><span className="sampling-plan-criteria-count">{groupEntries.reduce((sum, entry) => sum + (entry.location.acceptance_criteria || []).length, 0)} criteria · {groupEntries.reduce((sum, entry) => sum + (entry.location.acceptance_criteria || []).filter((criterion) => criterion.notes === 'Auto-fetched from Acceptance Criteria').length, 0)} linked</span></summary>
                          <div style={{ marginTop: '8px', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong style={{ fontSize: 'var(--fs-md)' }}>Acceptance Criteria</strong>
                            <button className="btn btn-outline btn-sm" type="button" onClick={() => openAddCriterionModal(groupEntries)} title="Add an extra criterion not covered by the standard set">
                              <FaPlus style={{ marginRight: '4px' }} />Add Criterion
                            </button>
                          </div>
                          <div className="table-container" style={{ marginTop: '6px' }}>
                            <table>
                              <thead>
                                <tr>
                                  <th>Sample Type</th>
                                  <th>Criterion</th>
                                  <th>Limit</th>
                                  <th>Unit</th>
                                  <th>Source</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {groupEntries.flatMap(({ location, index: locationIndex }) =>
                                  (location.acceptance_criteria || []).map((criterion, criterionIndex) => (
                                    <tr key={`${locationIndex}-${criterionIndex}`}>
                                      <td>{location.sample_type}{location.metadata?.interval_label ? ` (${location.metadata.interval_label})` : ''}</td>
                                      <td>{criterion.criterion_label || '—'}</td>
                                      <td>{criterion.limit_type === 'Visual' ? (criterion.limit_value === 'Fail' ? visualLabels.fail : visualLabels.pass) : (criterion.limit_value ?? '—')}</td>
                                      <td>{criterion.limit_type === 'Visual' ? '—' : (criterion.limit_unit || '—')}</td>
                                      <td>
                                        {criterion.notes === 'Auto-fetched from Acceptance Criteria' ? (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                            {criterion.override_justification?.trim() ? (
                                              <span title={criterion.override_justification} style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--warning-color, #b45309)' }}>Overridden (see tooltip)</span>
                                            ) : (
                                              <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--success-color)' }}>Auto-fetched</span>
                                            )}
                                            {isCriterionStale(criterion) && (
                                              <span title="The Acceptance Criteria master record has changed since this value was copied in" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, color: 'var(--warning-color, #b45309)' }}>⚠ Master updated</span>
                                            )}
                                            {canEdit && (
                                              <button type="button" className="btn btn-outline btn-sm" style={{ padding: '1px 6px', fontSize: 'var(--fs-2xs)' }}
                                                onClick={() => refreshCriterionFromMaster(locationIndex, criterionIndex)} title="Re-pull the latest value from the Acceptance Criteria master">
                                                Refresh
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>Manual</span>
                                        )}
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                          <button className="btn btn-outline btn-sm" type="button" onClick={() => openEditCriterionModal(locationIndex, criterionIndex, criterion)} title="Edit this criterion">
                                            <FaEdit />
                                          </button>
                                          <button className="btn btn-danger btn-sm" type="button" onClick={() => removeCriterion(locationIndex, criterionIndex)} title="Remove this criterion">X</button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                          </div>
                        </details>
                      )}
                      </div>
                    </details>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>


      <details className="card sampling-plan-advanced-card">
        <summary className="sampling-plan-advanced-summary">
          <span><strong>Rinse concentration reference</strong><small>Train-level equipment calculations</small></span>
          <span className="sampling-plan-summary-hint">{rinseRows.length} equipment rows</span>
        </summary>
        <div className="card-header"><h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Train Rinse Concentration Reference</h2></div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Equipment</th>
                <th>Surface Area (cm2)</th>
                <th>Rinse Volume (mL)</th>
                <th>Calculated Rinse Concentration (ppm)</th>
              </tr>
            </thead>
            <tbody>
              {rinseRows.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-muted">No equipment mapped to this train</td></tr>
              ) : rinseRows.map((row) => (
                <tr key={row.member_id}>
                  <td>{row.equipment_name}</td>
                  <td>{fmt(row.area, 2)}</td>
                  <td>{fmt(row.rinse_volume_ml, 2)}</td>
                  <td>{fmt(row.ppm, 6)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <details className="card sampling-plan-advanced-card">
        <summary className="sampling-plan-advanced-summary">
          <span><strong>Plan remarks</strong><small>Optional instructions and rationale summary</small></span>
          <span className="sampling-plan-summary-hint">{form.remarks ? 'Remarks added' : 'Optional'}</span>
        </summary>
        <div style={{ padding: '16px' }}>
          <label className="form-label">Plan Remarks</label>
          <textarea className="form-control" rows={4} value={form.remarks || ''} onChange={(e) => handleFormField('remarks', e.target.value)} placeholder="Sampling intimation expectations, rationale summary, or instructions for Micro / WQC / production team" />
        </div>
      </details>

      <ChangeReferenceModal
        open={changeRefGate}
        qmsLinked={qmsLinked}
        busy={changeRefBusy}
        recordLabel={form.plan_code}
        onCancel={() => setChangeRefGate(false)}
        onConfirm={confirmChangeReference}
      />

      {/* ── E-Sign Modal ──────────────────────────────────────────────────────── */}
      <ESignModal
        isOpen={showESign}
        title={eSignTitle}
        onCancel={closeESign}
        showDecision={eSignMode === 'product' || eSignMode === 'qa'}
        decisionLabel="Decision *"
        decisionOptions={APPROVAL_DECISIONS}
        decisionValue={eSignData.decision}
        onDecisionChange={(value) => setESignData((p) => ({ ...p, decision: value }))}
        commentLabel={`Reason / Comment ${eSignMode === 'returnToDraft' ? '*' : ''}`}
        commentValue={eSignData.comment}
        onCommentChange={(value) => setESignData((p) => ({ ...p, comment: value }))}
        commentPlaceholder="Provide reason or comment…"
        passwordValue={eSignData.password}
        onPasswordChange={(value) => setESignData((p) => ({ ...p, password: value }))}
        passwordRef={passwordRef}
        note={
          <div style={{ padding: '8px 12px', background: 'var(--bg-color)', borderRadius: '6px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>
            <strong>Signed by:</strong> {user.username || 'Unknown'} &nbsp;|&nbsp;
            <strong>Date/Time:</strong> {new Date().toLocaleString()} &nbsp;|&nbsp;
            <strong>Role:</strong> {user.role}
          </div>
        }
        onConfirm={handleConfirmESign}
        confirmDisabled={eSigning}
        maxWidth="500px"
        confirmLabel={eSigning ? 'Processing…' : (
          eSignMode === 'submit'        ? 'Confirm & Submit' :
          eSignMode === 'returnToDraft' ? 'Confirm Return to Draft' :
          eSignMode === 'delete'        ? 'Confirm Deactivate' :
          eSignMode === 'product'       ? 'Confirm Product Decision' :
                                           'Confirm QA Decision'
        )}
      />

      {/* ── Acceptance Criterion Modal (Add / Edit) ─────────────────────────────── */}
      {criterionModal && (
        <Modal isOpen size="md" title={criterionModal.mode === 'add' ? 'Add Acceptance Criterion' : 'Edit Acceptance Criterion'} onClose={closeCriterionModal}>
            <div className="modal-section">
              <h3 className="modal-section-title">Criterion</h3>
              <div className="form-grid">
              {criterionModal.mode === 'add' && criterionModal.groupEntries.length > 1 && (
                <div className="form-group">
                  <label className="form-label">Sample Type *</label>
                  <select className="form-control" value={criterionModal.groupEntries.find(({ index }) => index === criterionModal.locationIndex)?.location.sample_type || ''}
                    onChange={(e) => changeCriterionModalSampleType(e.target.value)}>
                    {criterionModal.groupEntries.map(({ location }) => (
                      <option key={location.sample_type} value={location.sample_type}>{location.sample_type}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Criterion Label *</label>
                <input className="form-control" value={criterionModal.draft.criterion_label || ''}
                  onChange={(e) => updateCriterionModalField('criterion_label', e.target.value)} placeholder="e.g. Residue Limit" />
              </div>
              <div className="form-group">
                <label className="form-label">Limit Type</label>
                <select className="form-control" value={criterionModal.draft.limit_type || 'Chemical'}
                  onChange={(e) => updateCriterionModalField('limit_type', e.target.value)}>
                  <option value="Chemical">Chemical</option>
                  <option value="Detergent">Detergent</option>
                  <option value="Microbiological">Microbiological</option>
                  <option value="TOC">TOC</option>
                  <option value="Visual">Visual</option>
                </select>
              </div>
              </div>
            </div>
            <div className="modal-section">
              <h3 className="modal-section-title">Limits &amp; Notes</h3>
              <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Limit Value</label>
                {criterionModal.draft.limit_type === 'Visual' ? (
                  <select className="form-control" value={criterionModal.draft.limit_value || 'Pass'} onChange={(e) => updateCriterionModalField('limit_value', e.target.value)}>
                    <option value="Pass">{visualLabels.pass}</option>
                    <option value="Fail">{visualLabels.fail}</option>
                  </select>
                ) : (
                  <input className="form-control" type="number" value={criterionModal.draft.limit_value ?? ''}
                    onChange={(e) => updateCriterionModalField('limit_value', e.target.value)} />
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Limit Unit</label>
                <input className="form-control" value={criterionModal.draft.limit_unit || ''} disabled={criterionModal.draft.limit_type === 'Visual'}
                  onChange={(e) => updateCriterionModalField('limit_unit', e.target.value)} placeholder="e.g. ppm, mcg/cm2, cfu/mL" />
              </div>
              <div className="form-group form-group-wide">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={criterionModal.draft.notes || ''}
                  onChange={(e) => updateCriterionModalField('notes', e.target.value)} placeholder="Optional notes" />
              </div>
              {criterionModal.defaultLimitValue !== null && criterionModal.defaultLimitValue !== undefined
                && String(criterionModal.draft.limit_value ?? '') !== String(criterionModal.defaultLimitValue ?? '') && (
                <div className="form-group form-group-wide">
                  <label className="form-label" style={{ color: 'var(--warning-text)' }}>
                    Justification for changing this limit (required) — default from Acceptance Criteria / MACO was {String(criterionModal.defaultLimitValue)}
                  </label>
                  <textarea className="form-control" rows={2} value={criterionModal.draft.override_justification || ''}
                    onChange={(e) => updateCriterionModalField('override_justification', e.target.value)}
                    placeholder="Explain why this limit differs from the Acceptance Criteria / MACO default..." />
                </div>
              )}
              </div>
            </div>
          <ModalFooter>
              <button className="btn btn-outline" onClick={closeCriterionModal}>Cancel</button>
              <button className="btn btn-primary" onClick={saveCriterionModal}>{criterionModal.mode === 'add' ? 'Add Criterion' : 'Save Changes'}</button>
          </ModalFooter>
        </Modal>
      )}

      <AIResultPanel
        isOpen={ai.isOpen}
        onClose={ai.close}
        title={ai.title}
        loading={ai.loading}
        error={ai.error}
        content={ai.content}
        docType="report"
        exportFileName="sampling-plans-ai-report.pdf"
      />

      {newPlanModal}
      <VersionHistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={form.plan_code ? `Version History — ${form.plan_code}` : 'Version History'}
        fetcher={() => samplingPlansAPI.getRevisions(form.plan_id)}
      />
    </div>
  );
};

export default SamplingPlans;
